import { Fragment, useState, type CSSProperties, type ReactNode } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { DateField, Modal } from "@fawkes/ui";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  Ban,
  BedDouble,
  Boxes,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Circle,
  CreditCard,
  Crown,
  Euro,
  FileSignature,
  FileText,
  Globe,
  History,
  HeartPulse,
  Info,
  LogIn,
  LogOut,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Package,
  PawPrint,
  Pencil,
  Plus,
  ReceiptText,
  RotateCcw,
  Search,
  Sticker,
  ShieldCheck,
  SlidersHorizontal,
  Syringe,
  Trash2,
  X,
  XCircle,
} from "lucide-react";

import { ProductTariefPicker, type GekozenProductRegel } from "../../components/ProductTariefPicker";
import { antwoordTekst } from "../pensionreservering/EigenElementen";
import { formatDateMetDag, formatEuro } from "../../i18n/format";
import type {
  PensionDier,
  PensionReservering,
  PensionReserveringDetail,
  ReserveringDetailStatus,
  ReserveringDier,
  ReserveringPeriode,
  ReserveringProduct,
  ReserveringTag,
  ReserveringWeging,
} from "../pensionKlantTypes";
import { RES_TOON } from "./BoekingTabs";
import { groepeerBoekingen, isAutomatisch, standaardBoeking, type Boeking } from "./boekingen";
import {
  LEEG,
  cijferDatum,
  dagenTussen,
  entingStaat,
  geslachtTeken,
  kenmerkToon,
  periode,
  type Context,
  type Staat,
} from "./hulp";
import { Laadstaat, Leeg, type PilToon } from "./Sectie";
import { DierVenster } from "./DierVenster";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/** De tinten van dit scherm: die van de pils, plus paars voor de luxe suite. */
type Toon = PilToon | "paars";
type Dagdeel = "VM" | "NM";
type ProductSoort = "DIER" | "RESERVERING";

const STATUS: Record<ReserveringDetailStatus, { toon: Toon; icoon: LucideIcon }> = {
  NIEUW: { toon: "accent", icoon: Circle },
  BEVESTIGD: { toon: "groen", icoon: CheckCircle2 },
  AFGEROND: { toon: "grijs", icoon: CheckCircle2 },
  GEANNULEERD: { toon: "rood", icoon: XCircle },
  ONBEKEND: { toon: "grijs", icoon: Circle },
};

const STAAT_TOON: Record<Staat, Toon> = {
  verlopen: "rood",
  binnenkort: "amber",
  inOrde: "groen",
  onbekend: "grijs",
};

/**
 * De bronnen van de productkiezer op een pensionreservering, in de volgorde van GET /api/pension/producten:
 * wat de balie eerder op pensionreserveringen zette, de pensiontarieven en de producten en diensten.
 */
const PENSION_BRONNEN = [
  { key: "", labelKey: "picker.bron.alle", icon: Globe },
  { key: "Eerder gebruikt bij pension", labelKey: "picker.bron.eerderPension", icon: History },
  { key: "Pension tarieven", labelKey: "picker.bron.pension", icon: BedDouble },
  { key: "Producten & Diensten", labelKey: "picker.bron.producten", icon: Boxes },
];

/** Zoveel verblijven staan er in de rechterkolom meteen; de rest op verzoek. */

/** Zoveel labels staan er per dier in de tabel; de rest achter een teller. */
const TAGS_IN_TABEL = 2;

/** Een luxe suite herken je aan de kennelgroep, de prijscategorie of een product met die naam. */
const LUXE = /luxe|suite/i;

/** Eén reservering in detail, uit GET /api/pension/reserveringen/{id}. */
export function useReservering(id: number | null) {
  return useCustom<PensionReserveringDetail>({
    url: `pension/reserveringen/${id}`,
    method: "get",
    queryOptions: { enabled: id != null },
  });
}

/**
 * Het tabblad Pension: één reservering zoals de balie hem opent, naar het ontwerp van de reserveringsdetail.
 * Links de kop, het verblijf met in- en uitchecken, de signalen, de dieren, de producten en het geld; rechts
 * de eerdere verblijven om naar te wisselen, de bijzonderheden en de documenten. Zonder keuze staat het
 * lopende verblijf open, anders het eerstvolgende, anders het laatste.
 */
export function PensionTab({
  reserveringen,
  dieren,
  fotos,
  laden,
  ctx,
  onKiesDier,
  onOpenFactuur,
  onGewijzigd,
  klantNaam,
  klantId,
  onOpenPlanning,
}: {
  reserveringen: PensionReservering[];
  /** De dieren van de klant: foto, kenmerken en entingen staan daar, niet op de reservering. */
  dieren: PensionDier[];
  fotos: Record<string, string | null | undefined>;
  laden: boolean;
  ctx: Context;
  onKiesDier: (id: number) => void;
  onOpenFactuur?: (factuurId: string) => void;
  /** De naam van de klant; die staat in het diervenster bij de reserveringsgegevens. */
  klantNaam?: string | null;
  /** De relatie waaraan de communicatielog hangt; de geschiedenis leest daaruit wat er verstuurd is. */
  klantId?: number | null;
  /** Opent de kennelplanning vanuit het diervenster. */
  onOpenPlanning?: () => void;
  /** Na bewerken, in- of uitchecken: de klant ververst mee, want zijn boekingen veranderen. */
  onGewijzigd?: () => void;
}) {
  const { t } = ctx;
  const boekingen = groepeerBoekingen(reserveringen);
  const [gekozen, setGekozen] = useState<number | null>(null);
  const boeking = boekingen.find((b) => b.reserveringId === gekozen) ?? standaardBoeking(boekingen);
  const { data, isError, refetch } = useReservering(boeking?.reserveringId ?? null);

  if (laden) return <Leeg>{t("common.loading")}</Leeg>;
  if (!boeking) {
    return (
      <div className="pk-paneel pk-paneel--leeg">
        <p>{t("pensionKlant.pension.geen")}</p>
      </div>
    );
  }

  // Bij het wisselen hoort de vorige reservering niet even onder de nieuwe kop te staan.
  const r = data?.data?.id === boeking.reserveringId ? data.data : undefined;
  const bezoek = boekingen.filter((b) => b.status !== "GEANNULEERD" && b.van < boeking.van).length + 1;
  const klantDieren = new Map(dieren.map((d) => [d.id, d]));
  const ververs = () => {
    void refetch();
    onGewijzigd?.();
  };

  // De kaarten die niet van de reservering zelf komen. Ze gaan als slot naar het detail, want dat
  // bepaalt wat er bovenaan de rechterkolom staat -- het geld, dat het bewerkconcept kent.
  const zijkolom = r ? (
    <>
      <EerdereVerblijven boekingen={boekingen} gekozen={boeking.reserveringId} onKies={setGekozen} ctx={ctx} />
      <Bijzonderheden key={r.id} r={r} klantDieren={klantDieren} ctx={ctx} onGewijzigd={ververs} />
      <Documenten r={r} ctx={ctx} onOpenFactuur={onOpenFactuur} />
      <Geschiedenis r={r} klantId={klantId} ctx={ctx} />
      {/* "Uit de wizard" staat niet in de mockup, maar draagt wel wat de klant online invulde; die
          weggooien zou gegevens verbergen die nergens anders staan. Daarom onderaan, buiten de
          volgorde die de mockup voorschrijft. */}
      <UitDeWizard key={`wizard-${r.id}`} r={r} ctx={ctx} />
    </>
  ) : null;

  return (
    <div className="pk-rv">
      {r ? (
        <ReserveringDetail
          key={r.id}
          r={r}
          bezoek={bezoek}
          klantDieren={klantDieren}
          fotos={fotos}
          ctx={ctx}
          onKiesDier={onKiesDier}
          onGewijzigd={ververs}
          klantNaam={klantNaam}
          onOpenPlanning={onOpenPlanning}
          zijkolom={zijkolom}
        />
      ) : (
        <>
          <div className="pk-rv__hoofd">
            <Laadstaat laden={!isError} fout={isError} onOpnieuw={() => refetch()} ctx={ctx} />
          </div>
          <div className="pk-rv__zij">
            <EerdereVerblijven boekingen={boekingen} gekozen={boeking.reserveringId} onKies={setGekozen} ctx={ctx} />
          </div>
        </>
      )}
    </div>
  );
}

type KlantDieren = Map<number, PensionDier>;

function reserveringsnummer(r: PensionReserveringDetail): string {
  return r.nummer != null ? `#R${r.nummer}` : `#${r.id}`;
}

// ── Bewerken ─────────────────────────────────────────────────────────────────────────────────────────

interface DierConcept {
  prijs: string;
  korting: string;
  klittenCheck: boolean;
  alleenInKennel: boolean;
  sociaalGeval: boolean;
  /** Gaat bij opslaan van de reservering af, met zijn producten. */
  verwijderd: boolean;
}

/** Haalt een dier van de reservering; nieuwe producten bij dat dier gaan naar de reservering als geheel. */
const verwijderDier = (regelId: number, dierId: number | null) => (x: Concept): Concept => ({
  ...x,
  dieren: { ...x.dieren, [regelId]: { ...x.dieren[regelId], verwijderd: true } },
  nieuweProducten: x.nieuweProducten.map((p) => (dierId != null && p.dierId === dierId ? { ...p, dierId: null } : p)),
});

interface ProductConcept {
  naam: string;
  prijs: string;
  aantal: string;
  btw: string;
  korting: string;
  verwijderd: boolean;
}

interface NieuwDierConcept extends DierConcept {
  dierId: number;
}

interface NieuwProductConcept {
  sleutel: string;
  /** Het dier waar het product bij hoort; {@code null} is de reservering als geheel. */
  dierId: number | null;
  naam: string;
  prijs: string;
  aantal: string;
  btw: string;
  korting: string;
}

/** Wat er tijdens het bewerken op het scherm staat; getallen als tekst, zoals ze in de velden staan. */
export interface Concept {
  van: string;
  vanDagdeel: Dagdeel;
  tot: string;
  totDagdeel: Dagdeel;
  akkoord: boolean;
  opmerking: string;
  dieren: Record<number, DierConcept>;
  producten: Record<string, ProductConcept>;
  nieuweDieren: NieuwDierConcept[];
  nieuweProducten: NieuwProductConcept[];
}

interface Bewerker {
  concept: Concept;
  zet: (f: (c: Concept) => Concept) => void;
  zetDier: <K extends keyof DierConcept>(regelId: number, veld: K, waarde: DierConcept[K]) => void;
  zetProduct: <K extends keyof ProductConcept>(sleutel: string, veld: K, waarde: ProductConcept[K]) => void;
}

const productSleutel = (soort: ProductSoort, id: number) => `${soort}:${id}`;

let volgnummer = 0;
const nieuweSleutel = () => `nieuw-${++volgnummer}`;

/** 12.5 → "12,5", zoals je een bedrag intypt. */
function alsTekst(n: number | null | undefined): string {
  return String(n ?? 0).replace(".", ",");
}

/** "12,50" of "12.50" → 12.5; een leeg veld is 0; onzin is {@code null}. */
function leesGetal(tekst: string): number | null {
  const waarde = tekst.trim().replace(",", ".");
  if (waarde === "") return 0;
  const n = Number(waarde);
  return Number.isFinite(n) ? n : null;
}

const getalOf0 = (tekst: string) => leesGetal(tekst) ?? 0;
const rond = (n: number) => Math.round(n * 100) / 100;

function productConcept(p: ReserveringProduct): ProductConcept {
  return {
    naam: p.naam,
    prijs: alsTekst(p.prijs),
    aantal: alsTekst(p.aantal),
    btw: alsTekst(p.btwPercentage),
    korting: alsTekst(p.korting),
    verwijderd: false,
  };
}

function alleProducten(r: PensionReserveringDetail): { soort: ProductSoort; p: ReserveringProduct }[] {
  return [
    ...r.dieren.flatMap((d) => d.producten.map((p) => ({ soort: "DIER" as ProductSoort, p }))),
    ...r.producten.map((p) => ({ soort: "RESERVERING" as ProductSoort, p })),
  ];
}

export function nieuwConcept(r: PensionReserveringDetail): Concept {
  return {
    van: r.gereserveerd.van ?? "",
    vanDagdeel: r.gereserveerd.vanDagdeel ?? "VM",
    tot: r.gereserveerd.tot ?? "",
    totDagdeel: r.gereserveerd.totDagdeel ?? "NM",
    akkoord: r.akkoordMetVoorwaarden,
    opmerking: r.opmerking ?? "",
    dieren: Object.fromEntries(
      r.dieren.map((d) => [
        d.regelId,
        {
          prijs: alsTekst(d.prijsLaag),
          korting: alsTekst(d.korting),
          klittenCheck: d.klittenCheck,
          alleenInKennel: d.alleenInKennel,
          sociaalGeval: d.sociaalGeval,
          verwijderd: false,
        },
      ]),
    ),
    producten: Object.fromEntries(alleProducten(r).map(({ soort, p }) => [productSleutel(soort, p.id), productConcept(p)])),
    nieuweDieren: [],
    nieuweProducten: [],
  };
}

/** De dagen zoals de api ze telt: de nachten, plus de extra dag bij ophalen in de middag. */
function conceptDagen(r: PensionReserveringDetail, c: Concept): number {
  if (!c.van || !c.tot) return 0;
  const telling = r.dagtelling;
  const extra =
    !!telling?.extraDagBijVertrekNm &&
    c.totDagdeel === "NM" &&
    !(telling.geenExtraDagBijAankomstNm && c.vanDagdeel === "NM");
  return Math.max(0, dagenTussen(c.van, c.tot) + (extra ? 1 : 0));
}

/** Wat er aan een concept schort, of {@code null} als het opgeslagen kan worden. */
function conceptFout(c: Concept): "ongeldig" | "laatsteDier" | null {
  if (Object.values(c.dieren).filter((d) => !d.verwijderd).length + c.nieuweDieren.length === 0) return "laatsteDier";
  return conceptOngeldig(c) ? "ongeldig" : null;
}

function conceptOngeldig(c: Concept): boolean {
  if (!c.van || !c.tot) return true;
  const moment = (datum: string, dagdeel: Dagdeel) => `${datum}T${dagdeel === "VM" ? "09" : "15"}`;
  if (moment(c.tot, c.totDagdeel) < moment(c.van, c.vanDagdeel)) return true;
  const fout = (tekst: string) => {
    const n = leesGetal(tekst);
    return n == null || n < 0;
  };
  const productFout = (p: { naam: string; prijs: string; aantal: string; btw: string; korting: string }) =>
    p.naam.trim() === "" || fout(p.prijs) || fout(p.aantal) || fout(p.btw) || fout(p.korting) || getalOf0(p.btw) > 100;
  if ([...Object.values(c.dieren), ...c.nieuweDieren].some((d) => fout(d.prijs) || fout(d.korting))) return true;
  return Object.values(c.producten).some((p) => !p.verwijderd && productFout(p)) || c.nieuweProducten.some(productFout);
}

/** Het verzoek voor PUT /api/pension/reserveringen/{id}: de periode altijd, verder alleen wat verandert. */
export function verzoekVan(r: PensionReserveringDetail, c: Concept) {
  type DierVerzoek = {
    regelId: number;
    prijs?: number;
    korting?: number;
    klittenCheck?: boolean;
    alleenInKennel?: boolean;
    sociaalGeval?: boolean;
    verwijderd?: boolean;
  };
  const dieren = r.dieren.flatMap((d): DierVerzoek[] => {
    const x = c.dieren[d.regelId];
    if (x.verwijderd) return [{ regelId: d.regelId, verwijderd: true }];
    const prijs = getalOf0(x.prijs);
    const korting = getalOf0(x.korting);
    const gewijzigd =
      prijs !== (d.prijsLaag ?? 0) ||
      korting !== (d.korting ?? 0) ||
      x.klittenCheck !== d.klittenCheck ||
      x.alleenInKennel !== d.alleenInKennel ||
      x.sociaalGeval !== d.sociaalGeval;
    return gewijzigd
      ? [{ regelId: d.regelId, prijs, korting, klittenCheck: x.klittenCheck, alleenInKennel: x.alleenInKennel, sociaalGeval: x.sociaalGeval }]
      : [];
  });
  const producten = alleProducten(r).flatMap(({ soort, p }) => {
    const x = c.producten[productSleutel(soort, p.id)];
    if (x.verwijderd) return [{ id: p.id, soort, verwijderd: true }];
    const waarden = {
      naam: x.naam.trim(),
      prijs: getalOf0(x.prijs),
      aantal: getalOf0(x.aantal),
      btwPercentage: getalOf0(x.btw),
      korting: getalOf0(x.korting),
    };
    const gewijzigd =
      waarden.naam !== p.naam ||
      waarden.prijs !== (p.prijs ?? 0) ||
      waarden.aantal !== (p.aantal ?? 0) ||
      waarden.btwPercentage !== (p.btwPercentage ?? 0) ||
      waarden.korting !== (p.korting ?? 0);
    return gewijzigd ? [{ id: p.id, soort, ...waarden, verwijderd: false }] : [];
  });
  return {
    versie: r.versie,
    van: c.van,
    vanDagdeel: c.vanDagdeel,
    tot: c.tot,
    totDagdeel: c.totDagdeel,
    akkoordMetVoorwaarden: c.akkoord,
    opmerking: c.opmerking,
    dieren,
    producten,
    nieuweDieren: c.nieuweDieren.map((n) => ({
      dierId: n.dierId,
      prijs: getalOf0(n.prijs),
      korting: getalOf0(n.korting),
      klittenCheck: n.klittenCheck,
      alleenInKennel: n.alleenInKennel,
      sociaalGeval: n.sociaalGeval,
    })),
    nieuweProducten: c.nieuweProducten.map((n) => ({
      dierId: n.dierId,
      naam: n.naam.trim(),
      prijs: getalOf0(n.prijs),
      aantal: getalOf0(n.aantal),
      btwPercentage: getalOf0(n.btw),
      korting: getalOf0(n.korting),
    })),
  };
}

type BewerkFout = "conflict" | "mislukt" | "ongeldig" | "laatsteDier";

function ReserveringDetail({
  r,
  bezoek,
  klantDieren,
  fotos,
  ctx,
  onKiesDier,
  onGewijzigd,
  klantNaam,
  onOpenPlanning,
  zijkolom,
}: {
  r: PensionReserveringDetail;
  bezoek: number;
  klantDieren: KlantDieren;
  fotos: Record<string, string | null | undefined>;
  ctx: Context;
  onKiesDier: (id: number) => void;
  onGewijzigd: () => void;
  klantNaam?: string | null;
  onOpenPlanning?: () => void;
  /** De kaarten die niet van de reservering zelf komen: eerdere verblijven, bijzonderheden, documenten. */
  zijkolom?: ReactNode;
}) {
  const { t, locale } = ctx;
  // Eén berekening voor de balk en de lijst: twee zou betekenen dat de balk "1 aandachtspunt" zegt
  // terwijl de lijst er twee toont.
  const sigs = signalen(r, klantDieren, ctx);
  const [concept, setConcept] = useState<Concept | null>(null);
  const [fout, setFout] = useState<BewerkFout | null>(null);
  const [dierKiezer, setDierKiezer] = useState(false);
  const [productKiezer, setProductKiezer] = useState(false);
  const [menu, setMenu] = useState(false);
  const [annuleerVenster, setAnnuleerVenster] = useState<"annuleren" | "terugdraaien" | null>(null);
  /** In- of uitchecken kan vanuit de kop en vanuit de verblijfkaart; het venster is hetzelfde. */
  const [checkVenster, setCheckVenster] = useState<CheckinSoort | null>(null);
  const { mutateAsync, isLoading: bezig } = useCustomMutation<PensionReserveringDetail>();
  const titel = t("pensionKlant.res.titel", { args: [reserveringsnummer(r)] });
  const status = STATUS[r.status];
  const geannuleerd = r.status === "GEANNULEERD";
  // Eén keer bepaald en aan beide plekken doorgegeven: zou de kop zijn eigen voorwaarde hebben, dan
  // biedt hij straks een knop aan die de kaart eronder uitgeschakeld toont.
  const kanInchecken = !concept && !geannuleerd && !r.ingecheckt;
  const kanUitchecken = !concept && !geannuleerd && r.ingecheckt && !r.uitgecheckt;
  const meta = [
    r.aangemaakt ? t("pensionKlant.res.aangemaaktOp", { args: [datumTijd(r.aangemaakt)] }) : null,
    r.bevestigd ? t("pensionKlant.res.bevestigdOp", { args: [datumTijd(r.bevestigd)] }) : null,
    r.vestiging,
  ].filter(Boolean);
  // Dieren van de klant die nog niet op de reservering staan, ook niet als nieuw dier.
  const kandidaten = [...klantDieren.values()].filter(
    (d) => !r.dieren.some((x) => x.dierId === d.id) && !concept?.nieuweDieren.some((n) => n.dierId === d.id),
  );

  const start = (aanpassen?: (c: Concept) => Concept) => {
    if (geannuleerd) return;
    setFout(null);
    setConcept((huidig) => {
      const basis = huidig ?? nieuwConcept(r);
      return aanpassen ? aanpassen(basis) : basis;
    });
  };
  const bewerker: Bewerker | null = concept
    ? {
        concept,
        zet: (f) => setConcept((c) => (c ? f(c) : c)),
        zetDier: (regelId, veld, waarde) =>
          setConcept((c) => (c ? { ...c, dieren: { ...c.dieren, [regelId]: { ...c.dieren[regelId], [veld]: waarde } } } : c)),
        zetProduct: (sleutel, veld, waarde) =>
          setConcept((c) =>
            c ? { ...c, producten: { ...c.producten, [sleutel]: { ...c.producten[sleutel], [veld]: waarde } } } : c,
          ),
      }
    : null;

  const voegDierToe = (dier: PensionDier) => {
    setDierKiezer(false);
    start((c) => ({
      ...c,
      nieuweDieren: [
        ...c.nieuweDieren,
        {
          dierId: dier.id,
          prijs: alsTekst(dier.prijs?.dagtariefLaag ?? 0),
          korting: "0",
          klittenCheck: false,
          alleenInKennel: false,
          sociaalGeval: false,
          verwijderd: false,
        },
      ],
    }));
  };
  const voegProductToe = (regel: GekozenProductRegel) =>
    start((c) => ({
      ...c,
      nieuweProducten: [
        ...c.nieuweProducten,
        {
          sleutel: nieuweSleutel(),
          dierId: null,
          naam: regel.omschrijving,
          prijs: alsTekst(regel.prijsInclBtw),
          aantal: alsTekst(regel.aantal),
          btw: alsTekst(regel.btwPercentage),
          korting: "0",
        },
      ],
    }));

  const opslaan = async () => {
    if (!concept) return;
    const schort = conceptFout(concept);
    if (schort) {
      setFout(schort);
      return;
    }
    setFout(null);
    try {
      await mutateAsync({
        url: `pension/reserveringen/${r.id}`,
        method: "put",
        values: verzoekVan(r, concept),
        successNotification: false,
        errorNotification: false,
      });
      setConcept(null);
      onGewijzigd();
    } catch (e) {
      const conflict = (e as { statusCode?: number } | null)?.statusCode === 409;
      setFout(conflict ? "conflict" : "mislukt");
      if (conflict) {
        setConcept(null);
        onGewijzigd();
      }
    }
  };

  return (
    <>
      <div className="pk-rv__hoofd">
      <section className="pk-rv-kop" aria-label={titel}>
        <div>
          <div className="pk-rv-kop__titel">
            <h2>{titel}</h2>
            <Pil toon={status.toon} icoon={status.icoon}>
              {t(`pensionKlant.res.status.${r.status}`)}
            </Pil>
            <Pil toon="accent" icoon={Info}>
              {bezoek === 1 ? t("pensionKlant.res.eersteBezoek") : t("pensionKlant.res.bezoek", { args: [bezoek] })}
            </Pil>
            {r.online && <Pil toon="grijs">{t("pensionKlant.res.online")}</Pil>}
            {/* Het aandachtspunt hoort bij de kop en niet in een eigen balk: het is een eigenschap van
                deze reservering, net als de status, en een hele rij ervoor inruimen kostte een
                schermhoogte voor meestal één regel. */}
            <Aandachtspil sigs={sigs} ctx={ctx} />
          </div>
          {meta.length > 0 && <p className="pk-rv-kop__meta">{meta.join("  |  ")}</p>}
        </div>
        <div className="pk-rv-kop__knoppen">
          {concept ? (
            <>
              <button
                type="button"
                className="pk-rv-knop"
                disabled={bezig}
                onClick={() => {
                  setConcept(null);
                  setFout(null);
                }}
              >
                <X size={15} aria-hidden="true" />
                {t("pensionKlant.res.bewerk.annuleren")}
              </button>
              <button type="button" className="pk-rv-knop pk-rv-knop--hoofd" disabled={bezig} onClick={() => void opslaan()}>
                <Check size={15} aria-hidden="true" />
                {t("pensionKlant.res.opslaan")}
              </button>
            </>
          ) : (
            <>
              {(kanInchecken || kanUitchecken) && (
                // De belangrijkste handeling van dit scherm hoort bij de andere knoppen te staan, niet
                // halverwege de pagina in de verblijfkaart.
                <button
                  type="button"
                  className="pk-rv-knop pk-rv-knop--hoofd"
                  onClick={() => setCheckVenster(kanInchecken ? "inchecken" : "uitchecken")}
                >
                  {kanInchecken ? <LogIn size={15} aria-hidden="true" /> : <LogOut size={15} aria-hidden="true" />}
                  {t(kanInchecken ? "pensionKlant.res.inchecken" : "pensionKlant.res.uitchecken")}
                </button>
              )}
              <button
                type="button"
                className="pk-rv-knop"
                disabled={geannuleerd}
                title={geannuleerd ? t("pensionKlant.res.bewerk.kanNiet") : undefined}
                onClick={() => start()}
              >
                <Pencil size={15} aria-hidden="true" />
                {t("pensionKlant.res.bewerken")}
              </button>
              <button
                type="button"
                className="pk-rv-knop pk-rv-knop--icoon"
                aria-label={t("pensionKlant.res.meer")}
                aria-haspopup="menu"
                aria-expanded={menu}
                onClick={() => setMenu((open) => !open)}
              >
                <MoreHorizontal size={16} aria-hidden="true" />
              </button>
              {menu && (
                <div className="pk-rv-menu" role="menu">
                  {geannuleerd ? (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenu(false);
                        setAnnuleerVenster("terugdraaien");
                      }}
                    >
                      <RotateCcw size={15} aria-hidden="true" />
                      {t("pensionKlant.res.menu.terugdraaien")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      data-toon="rood"
                      disabled={r.ingecheckt}
                      title={r.ingecheckt ? t("pensionKlant.res.menu.kanNietIngecheckt") : undefined}
                      onClick={() => {
                        setMenu(false);
                        setAnnuleerVenster("annuleren");
                      }}
                    >
                      <Ban size={15} aria-hidden="true" />
                      {t("pensionKlant.res.menu.annuleren")}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {fout && (
          <p className="pk-rv-kop__fout" role="alert">
            {t(`pensionKlant.res.bewerk.${fout}`)}
          </p>
        )}
      </section>
      <VerblijfBlok
        r={r}
        ctx={ctx}
        onGewijzigd={onGewijzigd}
        bewerker={bewerker}
        onBewerken={geannuleerd ? undefined : () => start()}
        venster={checkVenster}
        onVenster={setCheckVenster}
      />
      <DierenBlok
        r={r}
        klantDieren={klantDieren}
        fotos={fotos}
        ctx={ctx}
        onKiesDier={onKiesDier}
        bewerker={bewerker}
        onStart={geannuleerd ? undefined : start}
        onToevoegen={geannuleerd || kandidaten.length === 0 ? undefined : () => setDierKiezer(true)}
        onGewijzigd={onGewijzigd}
        klantNaam={klantNaam}
        onOpenPlanning={onOpenPlanning}
      />
      <ProductenBlok
        r={r}
        ctx={ctx}
        klantDieren={klantDieren}
        bewerker={bewerker}
        onStart={geannuleerd ? undefined : start}
        onToevoegen={geannuleerd ? undefined : () => setProductKiezer(true)}
      />
      </div>
      <div className="pk-rv__zij">
        {/* Het geld bovenaan de rechterkolom: dat is waar de balie na het openen als eerste kijkt, en
            het hoort bij de kerngegevens in de balk en niet onderaan naast een lange productentabel. */}
        <FinancieelBlok r={r} ctx={ctx} concept={concept} klantDieren={klantDieren} />
        <ReserveringsproductenKaart
          r={r}
          ctx={ctx}
          onBeheren={geannuleerd ? undefined : () => setProductKiezer(true)}
        />
        {zijkolom}
      </div>
      {annuleerVenster && (
        <AnnuleerVenster
          soort={annuleerVenster}
          r={r}
          ctx={ctx}
          onSluit={() => setAnnuleerVenster(null)}
          onKlaar={() => {
            setAnnuleerVenster(null);
            onGewijzigd();
          }}
          onMislukt={onGewijzigd}
        />
      )}
      {dierKiezer && (
        <Modal open title={t("pensionKlant.res.bewerk.dierKiezen")} onClose={() => setDierKiezer(false)}>
          <div className="pk">
            <p className="pk-rv-leeg">{t("pensionKlant.res.bewerk.dierKiezenUitleg")}</p>
            {kandidaten.length === 0 ? (
              <p className="pk-rv-leeg">{t("pensionKlant.res.bewerk.geenDierenOver")}</p>
            ) : (
              <DierKiezer
                kandidaten={kandidaten}
                fotos={fotos}
                ctx={ctx}
                onKies={voegDierToe}
              />
            )}
          </div>
        </Modal>
      )}
      <ProductTariefPicker
        open={productKiezer}
        onClose={() => setProductKiezer(false)}
        onPick={voegProductToe}
        t={t}
        locale={locale}
        feedPath="pension/producten"
        bronnen={PENSION_BRONNEN}
      />
    </>
  );
}

/** Annuleren of een annulering terugdraaien, na bevestiging; bij annuleren met een reden. */
function AnnuleerVenster({
  soort,
  r,
  ctx,
  onSluit,
  onKlaar,
  onMislukt,
}: {
  soort: "annuleren" | "terugdraaien";
  r: PensionReserveringDetail;
  ctx: Context;
  onSluit: () => void;
  onKlaar: () => void;
  /** Mislukt is meestal: iemand anders was net eerder, of het dier is er intussen. Het scherm ververst mee. */
  onMislukt: () => void;
}) {
  const { t } = ctx;
  const [reden, setReden] = useState("");
  const [fout, setFout] = useState(false);
  const { mutateAsync, isLoading: bezig } = useCustomMutation<PensionReserveringDetail>();
  const annuleren = soort === "annuleren";
  const nummer = reserveringsnummer(r);

  const bevestig = async () => {
    setFout(false);
    try {
      await mutateAsync({
        url: `pension/reserveringen/${r.id}/${annuleren ? "annuleren" : "annulering-terugdraaien"}`,
        method: "post",
        values: annuleren
          ? { versie: r.versie, reden: reden.trim() === "" ? null : reden.trim() }
          : { versie: r.versie },
        successNotification: false,
        errorNotification: false,
      });
      onKlaar();
    } catch {
      setFout(true);
      onMislukt();
    }
  };

  return (
    <Modal
      open
      title={
        annuleren
          ? t("pensionKlant.res.annuleren.titel", { args: [nummer] })
          : t("pensionKlant.res.terugdraaien.titel", { args: [nummer] })
      }
      onClose={onSluit}
    >
      <form
        className="pk pk-checkin"
        onSubmit={(e) => {
          e.preventDefault();
          void bevestig();
        }}
      >
        <p className="pk-rv-leeg">
          {annuleren ? t("pensionKlant.res.annuleren.uitleg") : t("pensionKlant.res.terugdraaien.uitleg")}
        </p>
        {annuleren && (
          <label className="pk-veld">
            <span>{t("pensionKlant.res.annuleren.reden")}</span>
            <textarea rows={3} maxLength={2000} value={reden} onChange={(e) => setReden(e.target.value)} />
          </label>
        )}
        {fout && (
          <p className="pk-voetnoot" data-soort="fout" role="alert">
            {t("pensionKlant.res.annuleren.mislukt")}
          </p>
        )}
        <div className="pk-checkin__voet">
          <button type="button" className="pk-knop" onClick={onSluit}>
            {t("pensionKlant.res.annuleren.terug")}
          </button>
          <button type="submit" className={annuleren ? "pk-knop pk-knop--gevaar" : "pk-knop pk-knop--hoofd"} disabled={bezig}>
            {annuleren ? <Ban size={15} aria-hidden="true" /> : <RotateCcw size={15} aria-hidden="true" />}
            {annuleren ? t("pensionKlant.res.annuleren.bevestig") : t("pensionKlant.res.terugdraaien.bevestig")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Bouwstenen ───────────────────────────────────────────────────────────────────────────────────────

function Kaart({
  titel,
  label,
  icoon: Icoon,
  aantal,
  actie,
  className,
  inklapbaar,
  venster,
  children,
}: {
  titel: string;
  /** De naam van het blok voor schermlezers, als de titel een aantal draagt. */
  label?: string;
  icoon: LucideIcon;
  aantal?: number;
  actie?: ReactNode;
  className?: string;
  /**
   * Dichtgeklapt tonen tot iemand hem opent.
   *
   * <p>Voor de kaarten onderaan de rechterkolom -- eerdere verblijven, bijzonderheden, documenten,
   * geschiedenis. Uitgeklapt duwen ze het geld en de producten van het scherm af, terwijl ze zelden
   * gelezen worden: je kijkt naar eerdere verblijven als je iets zoekt, niet als je de reservering
   * opent. Het aantal staat in de kop, dus je ziet zonder klikken of er iets in zit.
   */
  inklapbaar?: boolean;
  /**
   * Opent in een dialoog in plaats van uit te klappen.
   *
   * <p>Voor de kaarten die te veel dragen om in een kolom van driehonderd pixels te passen -- eerdere
   * verblijven en de geschiedenis. Uitgeklapt in de zijkolom liepen de dierpillen over de status heen
   * en moest je bij dertien verblijven scrollen door de halve pagina. In een venster is er breedte,
   * en de zijkolom houdt één regel per onderwerp.
   */
  venster?: boolean;
  children: ReactNode | ((sluit: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(!inklapbaar && !venster);
  const kop = (
    <>
      <span className="pk-rv-kaart__icoon" aria-hidden="true">
        <Icoon size={15} />
      </span>
      <h3>
        {titel}
        {aantal != null && <span className="pk-rv-kaart__aantal">{aantal}</span>}
      </h3>
      {actie}
    </>
  );
  if (venster) {
    return (
      <section className={className ? `pk-rv-kaart ${className}` : "pk-rv-kaart"} aria-label={label ?? titel}>
        <button
          type="button"
          className="pk-rv-kaart__kop pk-rv-kaart__kop--klik"
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
        >
          {kop}
          <ChevronRight className="pk-rv-kaart__chevron" size={16} aria-hidden="true" />
        </button>
        {open && (
          <Modal open size="wide" title={titel} onClose={() => setOpen(false)}>
            <div className="pk pk-rv">
              {typeof children === "function" ? children(() => setOpen(false)) : children}
            </div>
          </Modal>
        )}
      </section>
    );
  }
  if (inklapbaar) {
    return (
      <section className={className ? `pk-rv-kaart ${className}` : "pk-rv-kaart"} aria-label={label ?? titel}>
        <button
          type="button"
          className="pk-rv-kaart__kop pk-rv-kaart__kop--klik"
          aria-expanded={open}
          onClick={() => setOpen((x) => !x)}
        >
          {kop}
          <ChevronRight className="pk-rv-kaart__chevron" size={16} aria-hidden="true" data-open={open || undefined} />
        </button>
        {open && (typeof children === "function" ? children(() => setOpen(false)) : children)}
      </section>
    );
  }
  return (
    <section className={className ? `pk-rv-kaart ${className}` : "pk-rv-kaart"} aria-label={label ?? titel}>
      <header className="pk-rv-kaart__kop">
        {kop}
      </header>
      {typeof children === "function" ? children(() => {}) : children}
    </section>
  );
}

function Pil({ toon, icoon: Icoon, children }: { toon: Toon; icoon?: LucideIcon; children: ReactNode }) {
  return (
    <span className="pk-rv-pil" data-toon={toon}>
      {Icoon && <Icoon size={13} aria-hidden="true" />}
      {children}
    </span>
  );
}

/**
 * Een knop voor wat nog gebouwd wordt: hij staat er zoals in het ontwerp, maar doet nog niets. Niet
 * {@code disabled}, want dan valt hij weg tegen de rest; wel {@code aria-disabled} en een tooltip.
 */
function Knop({
  icoon: Icoon,
  titel,
  label,
  toon,
  className,
  children,
}: {
  icoon: LucideIcon;
  titel: string;
  label?: string;
  toon?: Toon;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={["pk-rv-knop", children ? null : "pk-rv-knop--icoon", className].filter(Boolean).join(" ")}
      data-toon={toon}
      aria-disabled="true"
      title={titel}
      aria-label={label}
    >
      <Icoon size={15} aria-hidden="true" />
      {children}
    </button>
  );
}

/** Een aangevinkt of leeg vakje, zoals op het formulier; alleen om te lezen. */
function Vink({ aan, children }: { aan: boolean; children: ReactNode }) {
  return (
    <span className="pk-rv-vink" data-aan={aan || undefined} role="checkbox" aria-checked={aan} aria-readonly="true">
      <span className="pk-rv-vink__vak" aria-hidden="true">
        {aan && <Check size={12} strokeWidth={3} />}
      </span>
      {children}
    </span>
  );
}

/** Hetzelfde vakje, maar aan te klikken: een echte checkbox of radio eronder. */
function Keuze({
  type,
  naam,
  aan,
  label,
  onKies,
  children,
}: {
  type: "checkbox" | "radio";
  naam?: string;
  aan: boolean;
  label?: string;
  onKies: (aan: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <label className="pk-rv-vink pk-rv-vink--klik" data-aan={aan || undefined}>
      <input type={type} name={naam} checked={aan} aria-label={label} onChange={(e) => onKies(e.target.checked)} />
      <span className="pk-rv-vink__vak" aria-hidden="true">
        {aan && <Check size={12} strokeWidth={3} />}
      </span>
      {children}
    </label>
  );
}

/** Een bedrag of aantal in een tabelcel tijdens het bewerken. */
function Getalveld({
  waarde,
  label,
  smal,
  onWijzig,
}: {
  waarde: string;
  label: string;
  smal?: boolean;
  onWijzig: (waarde: string) => void;
}) {
  const n = leesGetal(waarde);
  return (
    <input
      type="text"
      inputMode="decimal"
      className={smal ? "pk-rv-bedraginvoer pk-rv-bedraginvoer--smal" : "pk-rv-bedraginvoer"}
      aria-label={label}
      aria-invalid={n == null || n < 0 || undefined}
      value={waarde}
      onChange={(e) => onWijzig(e.target.value)}
    />
  );
}

// ── Verblijf & check-in/out ──────────────────────────────────────────────────────────────────────────

type CheckinSoort = "inchecken" | "uitchecken";

function VerblijfBlok({
  r,
  ctx,
  onGewijzigd,
  bewerker,
  onBewerken,
  venster,
  onVenster,
}: {
  r: PensionReserveringDetail;
  ctx: Context;
  onGewijzigd: () => void;
  bewerker: Bewerker | null;
  /** Het bewerken starten vanaf deze kaart; dezelfde handeling als de knop in de kop. */
  onBewerken?: () => void;
  /** Het openstaande in-/uitcheckvenster; alleen de kop opent het nog, maar het venster hoort bij deze kaart. */
  venster: CheckinSoort | null;
  onVenster: (soort: CheckinSoort | null) => void;
}) {
  const { t } = ctx;
  const setVenster = onVenster;
  const v = r.verbleven;
  const c = bewerker?.concept;

  const ingecheckt = r.incheck
    ? t("pensionKlant.res.ingechecktDoorOp", { args: [r.incheck.door ?? LEEG, datumTijd(r.incheck.moment)] })
    : r.ingecheckt && v?.van
      ? t("pensionKlant.res.ingechecktOp", { args: [momentCijfer(v.van, v.vanTijd)] })
      : t("pensionKlant.res.nietIngecheckt");
  const uitgecheckt = r.uitcheck
    ? t("pensionKlant.res.uitgechecktDoorOp", { args: [r.uitcheck.door ?? LEEG, datumTijd(r.uitcheck.moment)] })
    : r.uitgecheckt && v?.tot
      ? t("pensionKlant.res.uitgechecktOp", { args: [momentCijfer(v.tot, v.totTijd)] })
      : t("pensionKlant.res.nietUitgecheckt");

  return (
    <Kaart
      titel={t("pensionKlant.res.verblijfKort")}
      icoon={CalendarDays}
      className="pk-rv-verblijfkaart"
      actie={
        onBewerken && !bewerker ? (
          <button type="button" className="pk-rv-knop" onClick={onBewerken}>
            <Pencil size={14} aria-hidden="true" />
            {t("pensionKlant.res.wijzigen")}
          </button>
        ) : undefined
      }
    >
      <div className="pk-rv-verblijf__raster">
        <div className="pk-rv-periode">
          <h4>{t("pensionKlant.res.gereserveerd")}</h4>
          <div className="pk-rv-periode__velden">
            {bewerker && c ? (
              <>
                <Datumbewerking
                  label={t("pensionKlant.res.van")}
                  aria={t("pensionKlant.res.bewerk.vanDatum")}
                  naam={`res-${r.id}-van`}
                  datum={c.van}
                  dagdeel={c.vanDagdeel}
                  onDatum={(van) => bewerker.zet((x) => ({ ...x, van }))}
                  onDagdeel={(vanDagdeel) => bewerker.zet((x) => ({ ...x, vanDagdeel }))}
                  ctx={ctx}
                />
                <Datumbewerking
                  label={t("pensionKlant.res.tot")}
                  aria={t("pensionKlant.res.bewerk.totDatum")}
                  naam={`res-${r.id}-tot`}
                  datum={c.tot}
                  dagdeel={c.totDagdeel}
                  onDatum={(tot) => bewerker.zet((x) => ({ ...x, tot }))}
                  onDagdeel={(totDagdeel) => bewerker.zet((x) => ({ ...x, totDagdeel }))}
                  ctx={ctx}
                />
              </>
            ) : (
              <PeriodeBalk periode={r.gereserveerd} ctx={ctx} />
            )}
          </div>
          {/* Tijdens het bewerken staat de herberekende duur eronder. In de leesweergave staat hij op de
              balk zelf; tijdens het wijzigen is er geen balk, en juist dan wil je zien wat je aan het
              doen bent terwijl je de datums verzet. */}
          {bewerker && c && (
            <p className="pk-rv-duurregel">
              <CalendarDays size={14} aria-hidden="true" />
              {dagen(conceptDagen(r, c), ctx)}
            </p>
          )}
          <div className="pk-rv-periode__onder">
            {bewerker && c ? (
              <Keuze type="checkbox" aan={c.akkoord} onKies={(akkoord) => bewerker.zet((x) => ({ ...x, akkoord }))}>
                {t("pensionKlant.res.akkoord")}
              </Keuze>
            ) : (
              <Vink aan={r.akkoordMetVoorwaarden}>{t("pensionKlant.res.akkoord")}</Vink>
            )}
          </div>
        </div>
        <div className="pk-rv-periode">
          <h4>{t("pensionKlant.res.werkelijk")}</h4>
          <div className="pk-rv-periode__velden">
            {/* Lege datumvelden tonen bij een dier dat nog niet binnen is, leest als een formulier dat
                iemand vergeten is in te vullen. De regels eronder zeggen al dat er nog niet ingecheckt
                is; meer hoeft het niet te zijn. */}
            {v?.van ? <PeriodeBalk periode={v} ctx={ctx} /> : null}
          </div>
          <div className="pk-rv-periode__onder">
            <p className="pk-rv-status" data-aan={r.ingecheckt || undefined}>
              {r.ingecheckt ? <CheckCircle2 size={14} aria-hidden="true" /> : <Circle size={14} aria-hidden="true" />}
              {ingecheckt}
            </p>
            {r.incheck?.informatie && <p className="pk-rv-citaat">{r.incheck.informatie}</p>}
            <p className="pk-rv-status" data-aan={r.uitgecheckt || undefined}>
              {r.uitgecheckt ? <CheckCircle2 size={14} aria-hidden="true" /> : <Circle size={14} aria-hidden="true" />}
              {uitgecheckt}
            </p>
            {r.uitcheck?.informatie && <p className="pk-rv-citaat">{r.uitcheck.informatie}</p>}
          </div>
        </div>
      </div>
      {venster && (
        <CheckinVenster
          soort={venster}
          r={r}
          ctx={ctx}
          onSluit={() => setVenster(null)}
          onGelukt={() => {
            setVenster(null);
            onGewijzigd();
          }}
          onMislukt={onGewijzigd}
        />
      )}
    </Kaart>
  );
}

/**
 * De periode zoals de mockup hem toont: aankomst links, vertrek rechts, de duur op de lijn ertussen.
 *
 * <p><b>Dit verving nagebouwde invoervelden.</b> De leesweergave toonde een kader met een kalendericoon
 * en twee vinkjes -- precies wat een formulier is, terwijl er niets in te vullen viel. Wie dat ziet,
 * probeert erin te typen. Nu staat er wat het is: twee datums en een duur.
 *
 * <p>De duur staat erbij omdat je hem anders uit twee datums moet aftrekken terwijl iemand aan de
 * telefoon wacht.
 */
/**
 * Een dier van de klant kiezen om op de reservering te zetten.
 *
 * <p>Dezelfde rij als op de reservering zelf -- foto, naam, soort en ras -- want het is hetzelfde dier
 * en je herkent het aan zijn kop, niet aan zijn naam: een klant met drie labradors heeft er "Beer",
 * "Beertje" en "Bobby" bij staan.
 *
 * <p>Het zoekveld verschijnt pas vanaf acht dieren. Daaronder scheelt zoeken niets en staat er alleen
 * een leeg veld boven een lijst die je in één blik overziet.
 */
function DierKiezer({ kandidaten, fotos, ctx, onKies }: {
  kandidaten: PensionDier[];
  fotos: Record<string, string | null | undefined>;
  ctx: Context;
  onKies: (d: PensionDier) => void;
}) {
  const { t } = ctx;
  const [zoek, setZoek] = useState("");
  const term = zoek.trim().toLowerCase();
  const zichtbaar = term === ""
    ? kandidaten
    : kandidaten.filter((d) =>
        `${d.naam ?? ""} ${d.diersoort ?? ""} ${d.ras ?? ""}`.toLowerCase().includes(term));

  return (
    <>
      {kandidaten.length >= 8 && (
        <div className="pk-rv-kiezer__zoek">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            autoComplete="off"
            value={zoek}
            placeholder={t("pensionKlant.res.bewerk.dierZoeken")}
            aria-label={t("pensionKlant.res.bewerk.dierZoeken")}
            onChange={(e) => setZoek(e.target.value)}
          />
        </div>
      )}
      {zichtbaar.length === 0 ? (
        <p className="pk-rv-leeg">{t("pensionKlant.res.bewerk.geenDierGevonden")}</p>
      ) : (
        <ul className="pk-rv-kiezer">
          {zichtbaar.map((d) => {
            const foto = d.id != null ? fotos[String(d.id)] : null;
            return (
              <li key={d.id}>
                <button type="button" className="pk-rv-kiesknop" onClick={() => onKies(d)}>
                  {foto ? (
                    <img className="pk-rv-kiesknop__foto" src={foto} alt="" />
                  ) : (
                    <span className="pk-rv-kiesknop__foto" aria-hidden="true">
                      <PawPrint size={16} />
                    </span>
                  )}
                  <span className="pk-rv-kiesknop__tekst">
                    <strong>{d.naam ?? LEEG}</strong>
                    <span className="pk-rv-sub">{[d.diersoort, d.ras].filter(Boolean).join(" · ")}</span>
                  </span>
                  <ChevronRight className="pk-rv-kiesknop__pijl" size={16} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function PeriodeBalk({ periode, ctx }: { periode: ReserveringPeriode; ctx: Context }) {
  const { t, locale } = ctx;
  const dagdeel = (d: Dagdeel | null) => (d ? t(`pensionKlant.res.dagdeelUit.${d}`) : null);
  return (
    <div className="pk-rv-balk">
      <div className="pk-rv-balk__kant">
        <strong>{periode.van ? formatDateMetDag(periode.van, locale) : LEEG}</strong>
        <span>{[t("pensionKlant.res.aankomst"), dagdeel(periode.vanDagdeel)].filter(Boolean).join(" · ")}</span>
      </div>
      <div className="pk-rv-balk__lijn" aria-hidden="true">
        <span className="pk-rv-balk__punt" />
        <span className="pk-rv-balk__draad" />
        <span className="pk-rv-balk__duur">{duur(periode, ctx)}</span>
        <span className="pk-rv-balk__draad" />
        <span className="pk-rv-balk__punt" />
      </div>
      <div className="pk-rv-balk__kant pk-rv-balk__kant--rechts">
        <strong>{periode.tot ? formatDateMetDag(periode.tot, locale) : LEEG}</strong>
        <span>{[t("pensionKlant.res.vertrek"), dagdeel(periode.totDagdeel)].filter(Boolean).join(" · ")}</span>
      </div>
    </div>
  );
}

function Datumbewerking({
  label,
  aria,
  naam,
  datum,
  dagdeel,
  onDatum,
  onDagdeel,
  ctx,
}: {
  label: string;
  aria: string;
  naam: string;
  datum: string;
  dagdeel: Dagdeel;
  onDatum: (datum: string) => void;
  onDagdeel: (dagdeel: Dagdeel) => void;
  ctx: Context;
}) {
  const { t } = ctx;
  return (
    <div className="pk-rv-datumregel">
      <span className="pk-rv-datumregel__label">{label}</span>
      <DateField className="pk-rv-invoer pk-rv-invoer--datum"
        aria-label={aria}
        value={datum}
        onChange={(e) => onDatum(e.target.value)}
      />
      {(["VM", "NM"] as Dagdeel[]).map((deel) => (
        <Keuze
          key={deel}
          type="radio"
          naam={naam}
          aan={dagdeel === deel}
          label={`${aria} ${t(`pensionKlant.res.dagdeel.${deel}`)}`}
          onKies={(aan) => aan && onDagdeel(deel)}
        >
          {t(`pensionKlant.res.dagdeel.${deel}`)}
        </Keuze>
      ))}
    </div>
  );
}

/**
 * Het venster bij in- en uitchecken: wie het dier brengt of haalt en wat er gezegd of meegegeven is. Bij
 * inchecken ook het akkoord met de voorwaarden, als dat er nog niet was.
 */
function CheckinVenster({
  soort,
  r,
  ctx,
  onSluit,
  onGelukt,
  onMislukt,
}: {
  soort: CheckinSoort;
  r: PensionReserveringDetail;
  ctx: Context;
  onSluit: () => void;
  onGelukt: () => void;
  /** Mislukt is meestal: iemand anders was net eerder. Dan hoort het scherm dat ook te laten zien. */
  onMislukt: () => void;
}) {
  const { t } = ctx;
  const [informatie, setInformatie] = useState("");
  const [akkoord, setAkkoord] = useState(r.akkoordMetVoorwaarden);
  const [fout, setFout] = useState(false);
  const { mutateAsync, isLoading: bezig } = useCustomMutation<PensionReserveringDetail>();

  const opslaan = async () => {
    setFout(false);
    const tekst = informatie.trim() === "" ? null : informatie.trim();
    try {
      await mutateAsync({
        url: `pension/reserveringen/${r.id}/${soort}`,
        method: "post",
        values: soort === "inchecken" ? { informatie: tekst, akkoordMetVoorwaarden: akkoord } : { informatie: tekst },
        successNotification: false,
        errorNotification: false,
      });
      onGelukt();
    } catch {
      setFout(true);
      onMislukt();
    }
  };

  return (
    <Modal open title={t(`pensionKlant.res.checkin.titel.${soort}`, { args: [reserveringsnummer(r)] })} onClose={onSluit}>
      <form
        className="pk pk-checkin"
        onSubmit={(e) => {
          e.preventDefault();
          void opslaan();
        }}
      >
        <label className="pk-veld">
          <span>{t(`pensionKlant.res.checkin.info.${soort}`)}</span>
          <textarea rows={4} maxLength={2000} value={informatie} onChange={(e) => setInformatie(e.target.value)} />
        </label>
        {soort === "inchecken" && (
          <label className="pk-checkin__vink">
            <input
              type="checkbox"
              checked={akkoord}
              disabled={r.akkoordMetVoorwaarden}
              onChange={(e) => setAkkoord(e.target.checked)}
            />
            {t("pensionKlant.res.akkoord")}
          </label>
        )}
        {fout && (
          <p className="pk-voetnoot" data-soort="fout" role="alert">
            {t("pensionKlant.res.checkin.mislukt")}
          </p>
        )}
        <div className="pk-checkin__voet">
          <button type="button" className="pk-knop" onClick={onSluit}>
            {t("pensionKlant.res.checkin.annuleren")}
          </button>
          <button type="submit" className="pk-knop pk-knop--hoofd" disabled={bezig}>
            {soort === "inchecken" ? <LogIn size={15} aria-hidden="true" /> : <LogOut size={15} aria-hidden="true" />}
            {t(`pensionKlant.res.${soort}`)}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Signalen ─────────────────────────────────────────────────────────────────────────────────────────

/** Wat de balie in één oogopslag moet weten voordat de klant voor de deur staat. */
/**
 * Eén signaal over deze reservering: wat er speelt, in welke kleur, en of het aandacht vraagt.
 *
 * <p>{@code aandacht} is niet hetzelfde als een kleur. Een openstaand bedrag staat amber omdat het geld
 * nog moet komen, maar dat is bij een lopende reservering de normale gang van zaken en geen punt waar
 * iemand iets aan moet doen. Verlopen entingen wél. Zouden we op kleur tellen, dan zou de balk bij
 * elke reservering die nog niet betaald is roepen dat er iets mis is, en dan kijkt niemand er meer naar.
 */
interface Sig {
  sleutel: string;
  icoon: LucideIcon;
  toon: Toon;
  titel: string;
  sub?: string | null;
  /** Vraagt dit om actie? Alleen deze tellen mee in de kerngegevensbalk. */
  aandacht?: boolean;
}

/**
 * De signalen van een reservering, één keer berekend.
 *
 * <p>De balk bovenaan telt ze en de lijst toont ze. Twee berekeningen zouden betekenen dat de balk "1
 * aandachtspunt" zegt terwijl de lijst er twee toont, en dan gelooft de balie geen van beide meer.
 */
function signalen(r: PensionReserveringDetail, klantDieren: KlantDieren, ctx: Context): Sig[] {
  const { t, locale, vandaag } = ctx;
  const euro = (n: number | null) => formatEuro(n, locale);
  const eigen = r.dieren.flatMap((d) => {
    const dier = d.dierId != null ? klantDieren.get(d.dierId) : undefined;
    return dier ? [dier] : [];
  });
  const staten = eigen.map((d) => entingStaat(d, vandaag));
  const enting: Staat = staten.includes("verlopen")
    ? "verlopen"
    : staten.includes("binnenkort")
      ? "binnenkort"
      : staten.length > 0 && staten.every((s) => s === "inOrde")
        ? "inOrde"
        : "onbekend";
  const geldigTot = eigen
    .map((d) => d.stand?.entingVerlooptOp ?? d.stand?.entingGeldigTot ?? null)
    .filter((x): x is string => !!x)
    .sort()[0];
  const entingSub =
    enting === "inOrde"
      ? geldigTot
        ? t("pensionKlant.res.sig.geldigTot", { args: [cijferDatum(geldigTot)] })
        : null
      : eigen
          .filter((_, i) => staten[i] === enting)
          .map((d) => d.naam)
          .filter(Boolean)
          .join(", ") || null;

  const f = r.financieel;
  const a = r.aanbetaling;
  const acties: string[] = [];
  if (r.status !== "AFGEROND" && r.status !== "GEANNULEERD") {
    const zonderKennel = r.dieren.filter((d) => !d.kennel).map((d) => d.naam ?? LEEG);
    if (zonderKennel.length > 0) {
      acties.push(t("pensionKlant.res.sig.geenKennel", { args: [zonderKennel.join(", ")] }));
    }
    if (!r.akkoordMetVoorwaarden) acties.push(t("pensionKlant.res.sig.voorwaarden"));
  }
  const luxeDier = r.dieren.find((d) => LUXE.test(d.kennelGroep ?? "") || LUXE.test(d.prijsCategorie ?? ""));
  const luxeProduct = [...r.producten, ...r.dieren.flatMap((d) => d.producten)].find((p) => LUXE.test(p.naam));
  const luxe = luxeDier ? (LUXE.test(luxeDier.kennelGroep ?? "") ? luxeDier.kennelGroep : luxeDier.prijsCategorie) : luxeProduct?.naam;
  const receptie = echteNotities(r)[0]?.notitie ?? r.opmerking;

  const lijst: Sig[] = [
    {
      sleutel: "enting",
      icoon: Syringe,
      toon: STAAT_TOON[enting],
      titel: t(`pensionKlant.res.sig.enting.${enting}`),
      sub: entingSub,
      // Een dier met verlopen entingen mag het pension niet in; "binnenkort" is nog te regelen maar
      // moet wel gezien worden. Onbekend is geen constatering, alleen een leegte.
      aandacht: enting === "verlopen" || enting === "binnenkort",
    },
    // Zolang de aanbetaling ertoe doet gaat die voor: dat is waar de balie op moet letten. Daarna het
    // openstaande bedrag van de hele reservering.
    a && a.status !== "NIET_MEER_NODIG"
      ? {
          sleutel: "aanbetaling",
          icoon: Euro,
          toon: a.status === "VOLDAAN" ? "groen" : a.status === "VERLOPEN" ? "rood" : "amber",
          titel: t(`pensionKlant.res.sig.aanbetaling.${a.status}`),
          sub: [
            t("pensionKlant.res.sig.vereist", { args: [euro(a.status === "VOLDAAN" ? a.vereist : a.open)] }),
            a.status !== "VOLDAAN" && a.uiterlijk
              ? t("pensionKlant.res.sig.uiterlijk", { args: [cijferDatum(a.uiterlijk)] })
              : null,
          ]
            .filter(Boolean)
            .join(" · "),
          // Een verstreken termijn is een punt; een aanbetaling die nog mag komen niet.
          aandacht: a.status === "VERLOPEN",
        }
      : {
          sleutel: "geld",
          icoon: Euro,
          toon: f.openstaand > 0 ? "amber" : f.totaal > 0 ? "groen" : "grijs",
          titel:
            f.openstaand > 0
              ? t("pensionKlant.res.sig.open", { args: [euro(f.openstaand)] })
              : f.totaal > 0
                ? t("pensionKlant.res.sig.betaald")
                : t("pensionKlant.res.sig.geenBedrag"),
          sub: f.totaal > 0 ? t("pensionKlant.res.sig.vanTotaal", { args: [euro(f.totaal)] }) : null,
        },
    {
      sleutel: "receptie",
      icoon: Info,
      toon: "accent",
      titel: t("pensionKlant.res.sig.receptie"),
      sub: receptie ? eersteRegel(receptie) : t("pensionKlant.res.sig.geenBijzonderheden"),
    },
    {
      sleutel: "actie",
      icoon: AlertCircle,
      toon: acties.length > 0 ? "rood" : "grijs",
      titel: acties.length > 0 ? t("pensionKlant.res.sig.actie") : t("pensionKlant.res.sig.geenActie"),
      sub: acties.length > 0 ? acties.join(" · ") : null,
      aandacht: acties.length > 0,
    },
  ];
  if (luxe) {
    lijst.push({ sleutel: "luxe", icoon: Crown, toon: "paars", titel: t("pensionKlant.res.sig.luxe"), sub: luxe });
  }
  return lijst;
}

/**
 * Het aandachtspunt als pil in de kop, met de punten zelf eronder zodra je hem opent.
 *
 * <p>Dit verving eerst een brede signalenrij en daarna een kerngegevensbalk. Beide kostten een
 * schermhoogte voor informatie die meestal uit één regel bestaat. Hier staat hij waar de status ook
 * staat, want het is hetzelfde soort gegeven: iets wat je over deze reservering moet weten voordat je
 * verder leest.
 *
 * <p>Zonder punten is er geen pil. Een groene "niets aan de hand" naast de status zou meeklinken als
 * een derde statuspil, en dan moet je elke keer lezen dat er niets te lezen valt.
 */
function Aandachtspil({ sigs, ctx }: { sigs: Sig[]; ctx: Context }) {
  const { t } = ctx;
  const [open, setOpen] = useState(false);
  const punten = sigs.filter((s) => s.aandacht);
  if (punten.length === 0) {
    return null;
  }
  return (
    <>
      <button
        type="button"
        className="pk-rv-aandachtspil"
        data-testid="aandachtspunten"
        aria-expanded={open}
        onClick={() => setOpen((x) => !x)}
      >
        <AlertCircle size={14} aria-hidden="true" />
        {punten.length === 1
          ? t("pensionKlant.res.kern.aandachtEen")
          : t("pensionKlant.res.kern.aandacht", { args: [punten.length] })}
        <ChevronRight className="pk-rv-aandachtspil__chevron" size={13} aria-hidden="true"
          data-open={open || undefined} />
      </button>
      {open && (
        <ul className="pk-rv-kern__punten">
          {punten.map((x) => (
            <li key={x.sleutel} data-toon={x.toon}>
              <strong>{x.titel}</strong>
              {x.sub && <span>{x.sub}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Dieren ───────────────────────────────────────────────────────────────────────────────────────────

function DierenBlok({
  r,
  klantDieren,
  fotos,
  ctx,
  onKiesDier,
  bewerker,
  onStart,
  onToevoegen,
  onGewijzigd,
  klantNaam,
  onOpenPlanning,
}: {
  r: PensionReserveringDetail;
  klantDieren: KlantDieren;
  fotos: Record<string, string | null | undefined>;
  ctx: Context;
  onKiesDier: (id: number) => void;
  bewerker: Bewerker | null;
  /** Na het indelen in een hok: de reservering opnieuw ophalen. */
  onGewijzigd: () => void;
  /** De naam van de klant, voor de reserveringsgegevens in het diervenster. */
  klantNaam?: string | null;
  /** Opent de kennelplanning; leeg als het scherm dat niet kan. */
  onOpenPlanning?: () => void;
  onStart?: (aanpassen?: (c: Concept) => Concept) => void;
  /** Opent de keuze van een dier; leeg als er niets te kiezen valt. */
  onToevoegen?: () => void;
}) {
  const { t, locale, vandaag } = ctx;
  const c = bewerker?.concept;
  const aantal = r.dieren.length + (c?.nieuweDieren.length ?? 0);
  /** Het dier waarvan het venster openstaat, op regelId. */
  const [detail, setDetail] = useState<number | null>(null);
  const detailDier = r.dieren.find((x) => x.regelId === detail) ?? null;
  const zetNieuw = <K extends keyof NieuwDierConcept>(dierId: number, veld: K, waarde: NieuwDierConcept[K]) =>
    bewerker?.zet((x) => ({
      ...x,
      nieuweDieren: x.nieuweDieren.map((n) => (n.dierId === dierId ? { ...n, [veld]: waarde } : n)),
    }));
  const haalWeg = (dierId: number) =>
    bewerker?.zet((x) => ({
      ...x,
      nieuweDieren: x.nieuweDieren.filter((n) => n.dierId !== dierId),
      // Producten bij dat dier blijven staan, maar horen dan bij de reservering als geheel.
      nieuweProducten: x.nieuweProducten.map((p) => (p.dierId === dierId ? { ...p, dierId: null } : p)),
    }));
  const tagKeuzes = (
    naam: string,
    alleen: boolean,
    sociaal: boolean,
    zet: (veld: "alleenInKennel" | "sociaalGeval", aan: boolean) => void,
  ) => (
    <span className="pk-rv-tagkeuzes">
      <Keuze type="checkbox" aan={alleen} label={`${t("pensionKlant.res.alleenInKennel")} (${naam})`} onKies={(aan) => zet("alleenInKennel", aan)}>
        {t("pensionKlant.res.alleenInKennel")}
      </Keuze>
      <Keuze type="checkbox" aan={sociaal} label={`${t("pensionKlant.res.sociaalGeval")} (${naam})`} onKies={(aan) => zet("sociaalGeval", aan)}>
        {t("pensionKlant.res.sociaalGeval")}
      </Keuze>
    </span>
  );
  const foto = (dierId: number | null) => {
    const bron = dierId != null ? fotos[String(dierId)] : null;
    return bron ? (
      <img className="pk-rv-foto" src={bron} alt="" />
    ) : (
      <span className="pk-rv-foto" aria-hidden="true">
        <PawPrint size={16} />
      </span>
    );
  };
  // De rijen van de dierentabel: eerst de dieren van de reservering, dan wat er tijdens het bewerken
  // bij is gezet. Alles wat een cel van een rij moet weten wordt hier een keer uitgerekend; anders
  // zou elke cel het opnieuw doen.
  type TabelRij =
    | {
        soort: "bestaand";
        key: string;
        d: PensionReserveringDetail["dieren"][number];
        diersoort: string | null | undefined;
        ras: string | null | undefined;
        geslacht: string | null | undefined;
        naam: string;
        staat: Staat;
        weg: boolean;
        dagenAantal: number;
        bedrag: number;
        klit: boolean;
        alleen: boolean;
        sociaal: boolean;
        tags: Array<{ tekst: string; toon: Toon | undefined }>;
      }
    | {
        soort: "nieuw";
        key: string;
        n: NonNullable<typeof c>["nieuweDieren"][number];
        diersoort: string | null | undefined;
        ras: string | null | undefined;
        geslacht: string | null | undefined;
        naam: string;
        staat: Staat;
        weg: false;
        dagenAantal: number;
        bedrag: number;
        klit: boolean;
        alleen: boolean;
        sociaal: boolean;
        tags: Array<{ tekst: string; toon: Toon | undefined }>;
      };

  const tabelRijen: TabelRij[] = [
    ...r.dieren.map((d): TabelRij => {
      const eigen = d.dierId != null ? klantDieren.get(d.dierId) : undefined;
      const x = c?.dieren[d.regelId];
      const alleen = x ? x.alleenInKennel : d.alleenInKennel;
      const sociaal = x ? x.sociaalGeval : d.sociaalGeval;
      const dagenAantal = c ? conceptDagen(r, c) : (d.dagenLaag ?? 0) + (d.dagenHoog ?? 0) || r.gereserveerd.dagen;
      return {
        soort: "bestaand",
        key: String(d.regelId),
        d,
        diersoort: d.diersoort,
        ras: d.ras,
        geslacht: d.geslacht,
        naam: d.naam ?? LEEG,
        staat: eigen ? entingStaat(eigen, vandaag) : "onbekend",
        weg: !!x?.verwijderd,
        dagenAantal,
        bedrag: c && x ? dagenAantal * getalOf0(x.prijs) + (d.toeslag ?? 0) - getalOf0(x.korting) : d.verblijfBedrag,
        klit: x ? x.klittenCheck : d.klittenCheck,
        alleen,
        sociaal,
        tags: [
          ...(eigen?.kenmerken ?? []).map((k) => ({ tekst: k, toon: tagToon(kenmerkToon(k)) })),
          ...(alleen ? [{ tekst: t("pensionKlant.res.alleenInKennel"), toon: "amber" as Toon }] : []),
          ...(sociaal ? [{ tekst: t("pensionKlant.res.sociaalGeval"), toon: "accent" as Toon }] : []),
          ...[d.wassenSoort, d.borstelSoort]
            .filter((w): w is string => !!w)
            .map((w) => ({ tekst: w, toon: undefined as Toon | undefined })),
        ],
      };
    }),
    ...(c?.nieuweDieren ?? []).map((n): TabelRij => {
      const dier = klantDieren.get(n.dierId);
      const dagenAantal = c ? conceptDagen(r, c) : 0;
      return {
        soort: "nieuw",
        key: `nieuw-${n.dierId}`,
        n,
        diersoort: dier?.diersoort,
        ras: dier?.ras,
        geslacht: dier?.geslacht,
        naam: dier?.naam ?? LEEG,
        staat: dier ? entingStaat(dier, vandaag) : "onbekend",
        weg: false,
        dagenAantal,
        bedrag: dagenAantal * getalOf0(n.prijs) - getalOf0(n.korting),
        klit: n.klittenCheck,
        alleen: n.alleenInKennel,
        sociaal: n.sociaalGeval,
        tags: [],
      };
    }),
  ];

  const geslacht = (waarde: string | null | undefined) => {
    const teken = geslachtTeken(waarde);
    return (
      <>
        {teken && (
          <span className="pk-rv-geslacht" data-g={teken === "♂" ? "m" : "v"} aria-hidden="true">
            {teken}
          </span>
        )}
        {waarde ?? LEEG}
      </>
    );
  };

  return (
    <Kaart
      titel={t("pensionKlant.res.dierenAantal", { args: [aantal] })}
      label={t("pensionKlant.res.dieren")}
      icoon={PawPrint}
      actie={
        onToevoegen ? (
          <button type="button" className="pk-rv-knop pk-rv-knop--accent" onClick={onToevoegen}>
            <Plus size={15} aria-hidden="true" />
            {t("pensionKlant.res.dierToevoegen")}
          </button>
        ) : (
          <Knop icoon={Plus} titel={t("pensionKlant.res.bewerk.geenDierenOver")} className="pk-rv-knop--accent">
            {t("pensionKlant.res.dierToevoegen")}
          </Knop>
        )
      }
    >
      {detailDier && (
        <DierVenster
          r={r}
          dier={detailDier}
          eigenDier={detailDier.dierId != null ? klantDieren.get(detailDier.dierId) : undefined}
          foto={detailDier.dierId != null ? fotos[String(detailDier.dierId)] : null}
          fotos={fotos}
          klantNaam={klantNaam}
          ctx={ctx}
          onSluit={() => setDetail(null)}
          onKiesDier={onKiesDier}
          onGeplaatst={onGewijzigd}
          onOpenPlanning={onOpenPlanning}
        />
      )}
      {aantal === 0 ? (
        <p className="pk-rv-leeg">{t("pensionKlant.res.geenDieren")}</p>
      ) : !bewerker ? (
        <DierKaarten
          r={r}
          fotos={fotos}
          ctx={ctx}
          onBoeking={setDetail}
        />
      ) : (
        <div className="pk-rv-tabelvak">
          <IndeelbareTabel
            tabelKey="pension-reservering-dieren"
            columns={[
              {
                key: "foto", header: t("pensionKlant.res.kol.foto"), vast: true,
                cell: (rij) => foto(rij.soort === "bestaand" ? rij.d.dierId : rij.n.dierId),
              },
              {
                key: "naam", header: t("pensionKlant.res.kol.naam"),
                cell: (rij) => (
                  <span className="pk-rv-naam">
                    <strong>{rij.naam}</strong>
                    {rij.soort === "nieuw" ? (
                      <Pil toon="accent">{t("pensionKlant.res.bewerk.nieuw")}</Pil>
                    ) : rij.d.dierId != null ? (
                      <button
                        type="button"
                        className="pk-rv-bekijk"
                        aria-label={t("pensionKlant.res.bekijkAria", { args: [rij.d.naam ?? ""] })}
                        onClick={() => onKiesDier(rij.d.dierId!)}
                      >
                        {t("pensionKlant.res.bekijk")}
                        <ChevronRight size={11} aria-hidden="true" />
                      </button>
                    ) : null}
                  </span>
                ),
              },
              {
                key: "dierRas", header: t("pensionKlant.res.kol.dierRas"),
                cell: (rij) => (
                  <>
                    {rij.diersoort ?? LEEG}
                    {rij.ras && <span className="pk-rv-sub">{rij.ras}</span>}
                  </>
                ),
              },
              {
                key: "geslacht", header: t("pensionKlant.res.kol.geslacht"),
                cell: (rij) => <span className="pk-rv-nowrap">{geslacht(rij.geslacht)}</span>,
              },
              {
                key: "startgewicht", header: t("pensionKlant.res.kol.startgewicht"),
                // Een dier dat er pas bij komt is nog niet gewogen.
                cell: (rij) => (rij.soort === "bestaand"
                  ? <span className="pk-rv-nowrap"><Gewicht w={rij.d.startgewicht} locale={locale} /></span>
                  : LEEG),
              },
              {
                key: "huidigGewicht", header: t("pensionKlant.res.kol.huidigGewicht"),
                cell: (rij) => (rij.soort === "bestaand"
                  ? <span className="pk-rv-nowrap"><Gewicht w={rij.d.huidigGewicht} locale={locale} /></span>
                  : LEEG),
              },
              {
                key: "kennelKamer", header: t("pensionKlant.res.kol.kennelKamer"),
                cell: (rij) => (rij.soort === "bestaand" && rij.d.kennel ? (
                  <>
                    <span className="pk-rv-sterk">{rij.d.kennel}</span>
                    {rij.d.kennelGroep && <span className="pk-rv-sub">({rij.d.kennelGroep})</span>}
                  </>
                ) : (
                  <span className="pk-rv-zacht">{t("pensionKlant.res.geenKennel")}</span>
                )),
              },
              {
                key: "verblijf", header: t("pensionKlant.res.kol.verblijf"),
                cell: (rij) => (
                  <span className="pk-rv-nowrap">
                    <span className="pk-rv-sterk">{dagen(rij.dagenAantal, ctx)}</span>
                    <span className="pk-rv-sub">{formatEuro(rij.bedrag, locale)}</span>
                  </span>
                ),
              },
              {
                key: "entingen", header: t("pensionKlant.res.kol.entingen"),
                cell: (rij) => (
                  <Pil toon={STAAT_TOON[rij.staat]} icoon={rij.staat === "inOrde" ? CheckCircle2 : undefined}>
                    {t(`pensionKlant.res.staat.${rij.staat}`)}
                  </Pil>
                ),
              },
              {
                key: "klitvrij", header: t("pensionKlant.res.kol.klitvrij"),
                cell: (rij) => (
                  <span className="pk-rv-nowrap">
                    {rij.soort === "nieuw" ? (
                      <Keuze
                        type="checkbox"
                        aan={rij.n.klittenCheck}
                        label={t("pensionKlant.res.bewerk.klitvrijVan", { args: [rij.naam] })}
                        onKies={(aan) => zetNieuw(rij.n.dierId, "klittenCheck", aan)}
                      >
                        {t("pensionKlant.res.klitvrij")}
                      </Keuze>
                    ) : bewerker && !rij.weg ? (
                      <Keuze
                        type="checkbox"
                        aan={rij.klit}
                        label={t("pensionKlant.res.bewerk.klitvrijVan", { args: [rij.naam] })}
                        onKies={(aan) => bewerker.zetDier(rij.d.regelId, "klittenCheck", aan)}
                      >
                        {t("pensionKlant.res.klitvrij")}
                      </Keuze>
                    ) : rij.klit ? (
                      <span className="pk-rv-ja">
                        <CheckCircle2 size={14} aria-hidden="true" />
                        {t("pensionKlant.res.klitvrij")}
                      </span>
                    ) : (
                      <span className="pk-rv-zacht">{t("pensionKlant.res.nietGecontroleerd")}</span>
                    )}
                  </span>
                ),
              },
              {
                key: "tags", header: t("pensionKlant.res.kol.tags"),
                cell: (rij) => {
                  if (rij.soort === "nieuw") {
                    return tagKeuzes(rij.naam, rij.n.alleenInKennel, rij.n.sociaalGeval,
                      (veld, aan) => zetNieuw(rij.n.dierId, veld, aan));
                  }
                  if (bewerker && !rij.weg) {
                    return tagKeuzes(rij.naam, rij.alleen, rij.sociaal,
                      (veld, aan) => bewerker.zetDier(rij.d.regelId, veld, aan));
                  }
                  if (rij.tags.length === 0) return LEEG;
                  /* Twee kenmerken en een teller: de hele lijst staat rechts bij Bijzonderheden. */
                  return (
                    <span className="pk-rv-tags">
                      {rij.tags.slice(0, TAGS_IN_TABEL).map((w) => (
                        <span key={w.tekst} className="pk-rv-tag" data-toon={w.toon}>
                          {w.tekst}
                        </span>
                      ))}
                      {rij.tags.length > TAGS_IN_TABEL && (
                        <span className="pk-rv-tag" title={rij.tags.slice(TAGS_IN_TABEL).map((w) => w.tekst).join(", ")}>
                          {t("pensionKlant.res.meerTags", { args: [rij.tags.length - TAGS_IN_TABEL] })}
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                key: "acties", header: t("pensionKlant.res.kol.acties"), vast: true, align: "right",
                cell: (rij) => (rij.soort === "nieuw" ? (
                  <RijActies ctx={ctx} onVerwijder={() => haalWeg(rij.n.dierId)} />
                ) : (
                  <RijActies
                    ctx={ctx}
                    onBekijk={bewerker ? undefined : () => setDetail(rij.d.regelId)}
                    onVerwijder={
                      r.ingecheckt || !onStart
                        ? undefined
                        : () =>
                            bewerker
                              ? bewerker.zet(verwijderDier(rij.d.regelId, rij.d.dierId))
                              : onStart(verwijderDier(rij.d.regelId, rij.d.dierId))
                    }
                    verwijderd={rij.weg}
                    onTerug={bewerker ? () => bewerker.zetDier(rij.d.regelId, "verwijderd", false) : undefined}
                    verwijderTitel={t("pensionKlant.res.bewerk.dierIngecheckt")}
                  />
                )),
              },
            ]}
            rows={tabelRijen}
            getRowKey={(rij) => rij.key}
            rowClassName={(rij) => (rij.soort === "nieuw" ? "pk-rv-nieuwrij" : rij.weg ? "pk-rv-weg" : undefined)}
            empty=""
          />
        </div>
      )}
    </Kaart>
  );
}

/**
 * Bewerken en verwijderen op een regel. Zonder handler blijft een knop staan zoals in het ontwerp, maar
 * uitgeschakeld met een tooltip waarom.
 */
/**
 * De dieren van deze reservering als kaarten, zoals in de mockup van 30-09-2026.
 *
 * <p><b>Waarom er twee weergaven zijn.</b> De tabel heeft twaalf kolommen en is tijdens het bewerken
 * een invulformulier: prijs, korting, klitvrij, alleen in kennel, sociaal geval, en een regel die je
 * eruit haalt. De mockup toont een leesweergave -- foto, naam, ras, arrangement, kamer, prijs, en een
 * knop naar het dier -- zonder invoervelden. Dat zijn twee verschillende dingen en niet twee smaken van
 * hetzelfde: kijken is het gewone geval, bewerken is de uitzondering. Zodra je Bewerken kiest komt de
 * tabel terug, met alles erin.
 *
 * <p>Wat de mockup niet toont maar de tabel wel weet -- geslacht, entingen, klitvrij, gewicht -- staat
 * op een tweede regel in de kaart. Weglaten zou het in de leesweergave onvindbaar maken, en juist de
 * entingen zijn waar een pension een dier op weigert.
 */
function DierKaarten({
  r,
  fotos,
  ctx,
  onBoeking,
}: {
  r: PensionReserveringDetail;
  fotos: Record<string, string | null | undefined>;
  ctx: Context;
  /** Opent de instellingen van dit dier op deze boeking: hok, arrangement, tarief. */
  onBoeking: (regelId: number) => void;
}) {
  const { t, locale } = ctx;
  return (
    <ul className="pk-rv-dierkaarten">
      {r.dieren.map((d) => {
        // De entingstaat stond op een tweede regel onder elke kaart; die is weg. Verlopen entingen
        // blijven zichtbaar als aandachtspunt in de kop, en dat is de plek waar ze iets moeten doen.
        const foto = d.dierId != null ? fotos[String(d.dierId)] : null;
        return (
          <li key={d.regelId} className="pk-rv-dierkaart">
            {foto ? (
              <img className="pk-rv-dierkaart__foto" src={foto} alt="" />
            ) : (
              <span className="pk-rv-dierkaart__foto" aria-hidden="true">
                <PawPrint size={20} />
              </span>
            )}
            <div className="pk-rv-dierkaart__naam">
              <strong>{d.naam ?? LEEG}</strong>
              <span>{d.ras ?? d.diersoort ?? LEEG}</span>
            </div>
            {/* De arrangementen die voor dit dier geboekt zijn, als chips. Ze stonden als regels in de
                productentabel; daar is "welke arrangementen heeft Beertje?" pas te beantwoorden na het
                aflopen van elf regels. Staat er geen arrangement, dan valt het terug op wat voor
                verblijf het is -- de prijscategorie, of anders de kennelgroep. */}
            <div className="pk-rv-dierkaart__veld">
              <span className="pk-rv-dierkaart__label">
                {arrangementen(d).length > 1
                  ? t("pensionKlant.res.kol.arrangementen")
                  : t("pensionKlant.res.kol.arrangement")}
              </span>
              {arrangementen(d).length > 0 ? (
                <span className="pk-rv-dierkaart__chips">
                  {arrangementen(d).map((a) => (
                    <span key={a} className="pk-rv-chip">{a}</span>
                  ))}
                </span>
              ) : (
                <strong>{d.prijsCategorie ?? d.kennelGroep ?? LEEG}</strong>
              )}
            </div>
            <Veld label={t("pensionKlant.res.kol.kennelKamer")} waarde={d.kennel ?? LEEG} />
            <Veld label={t("pensionKlant.res.kol.prijs")} waarde={formatEuro(d.verblijfBedrag, locale)} />
            {/* Twee verschillende bestemmingen, en die moeten allebei bereikbaar blijven: het dossier van
                het dier, en wat er op déze reservering voor dit dier geboekt is. De mockup toont alleen
                de eerste; de tweede stond in de rijknoppen van de tabel en zou anders in de
                leesweergave verdwijnen. */}
            {/* Eén knop, en die gaat naar de instellingen van dit dier op déze boeking: het hok, het
                arrangement, het tarief. Het dossier van het dier zelf staat in dat venster, dus er gaat
                niets verloren door hier maar één weg te bieden. */}
            <button
              type="button"
              className="pk-rv-knop"
              onClick={() => onBoeking(d.regelId)}
              aria-label={t("pensionKlant.res.dier.instellingenVan", { args: [d.naam ?? ""] })}
            >
              <SlidersHorizontal size={15} aria-hidden="true" />
              {t("pensionKlant.res.dier.instellingen")}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * De arrangementen van een dier op deze reservering.
 *
 * <p>Uit zijn eigen producten, op de groep. Het dossier noemt een arrangement geen eigen soort maar een
 * product in de groep "Arrangement"; dat is waar de productentabel ze ook vandaan haalt, dus beide
 * plekken tonen hetzelfde zonder een tweede regel die kan afwijken.
 */
function arrangementen(d: ReserveringDier): string[] {
  return d.producten.filter((p) => /arrangement/i.test(p.groep ?? "")).map((p) => p.naam);
}

function Veld({ label, waarde }: { label: string; waarde: string }) {
  return (
    <span className="pk-rv-dierkaart__veld">
      <span className="pk-rv-dierkaart__label">{label}</span>
      <strong>{waarde}</strong>
    </span>
  );
}


function RijActies({
  ctx,
  onBewerk,
  onBekijk,
  onVerwijder,
  verwijderd,
  onTerug,
  verwijderTitel,
}: {
  ctx: Context;
  onBewerk?: () => void;
  /** Opent wat er voor dit dier geboekt is en waar het ligt. */
  onBekijk?: () => void;
  onVerwijder?: () => void;
  verwijderd?: boolean;
  onTerug?: () => void;
  verwijderTitel?: string;
}) {
  const { t } = ctx;
  if (verwijderd && onTerug) {
    return (
      <span className="pk-rv-rijacties">
        <button type="button" className="pk-rv-knop pk-rv-knop--klein" onClick={onTerug}>
          <RotateCcw size={13} aria-hidden="true" />
          {t("pensionKlant.res.bewerk.terugzetten")}
        </button>
      </span>
    );
  }
  return (
    <span className="pk-rv-rijacties">
      {onBekijk && (
        <button
          type="button"
          className="pk-rv-rijknop"
          aria-label={t("pensionKlant.res.dier.bekijkAria")}
          title={t("pensionKlant.res.dier.bekijkAria")}
          onClick={onBekijk}
        >
          <ClipboardList size={13} aria-hidden="true" />
        </button>
      )}
      {onBewerk && (
        <button type="button" className="pk-rv-rijknop" aria-label={t("pensionKlant.res.bewerkRegel")} onClick={onBewerk}>
          <Pencil size={13} aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className="pk-rv-rijknop"
        data-toon="rood"
        aria-disabled={onVerwijder ? undefined : "true"}
        title={onVerwijder ? undefined : (verwijderTitel ?? t("pensionKlant.res.volgt"))}
        aria-label={t("pensionKlant.res.verwijderRegel")}
        onClick={onVerwijder}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </span>
  );
}

function Gewicht({ w, locale }: { w: ReserveringWeging | null; locale: string }) {
  if (!w || w.kg == null) return <>{LEEG}</>;
  return (
    <span title={w.datum ? cijferDatum(w.datum) : undefined}>
      {new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(w.kg)} kg
    </span>
  );
}

// ── Producten en geld ────────────────────────────────────────────────────────────────────────────────

interface ProductRegel {
  sleutel: string;
  /** Het verblijf van een dier, een product bij een dier of bij de reservering, of een nieuw product. */
  soort: "VERBLIJF" | ProductSoort | "NIEUW";
  /** De regel van het dier bij een bestaand verblijf, anders het id van het product. */
  bronId: number;
  /** Bij het verblijf van een dier dat erbij komt: dat dier. */
  nieuwDierId?: number;
  voor: string;
  omschrijving: string;
  /** Wat voor soort het is: Arrangement, Pensionverblijf, Verzekering. */
  groep: string | null;
  prijs: number | null;
  aantal: number | null;
  btw: number | null;
  korting: number | null;
  totaal: number;
  verwijderd: boolean;
}

/**
 * Alles wat op de reservering staat, per dier en daarna voor de reservering als geheel, zoals in het
 * ontwerp: eerst het verblijf zelf (dagen maal prijs), dan de producten bij dat dier. Tijdens het bewerken
 * met de waarden uit het concept, inclusief wat erbij komt.
 */
function productRegels(
  r: PensionReserveringDetail,
  { t }: Context,
  c: Concept | null,
  klantDieren: KlantDieren,
): ProductRegel[] {
  const regels: ProductRegel[] = [];
  const verblijfNaam = (categorie: string | null | undefined) =>
    categorie ? t("pensionKlant.res.verblijfCategorie", { args: [categorie] }) : t("pensionKlant.res.verblijfRegel");
  const verblijfBtw = r.dieren.find((d) => d.btwPercentage != null)?.btwPercentage ?? 21;
  const product = (soort: ProductSoort, p: ReserveringProduct, voor: string): ProductRegel => {
    const x = c?.producten[productSleutel(soort, p.id)];
    const prijs = x ? getalOf0(x.prijs) : p.prijs;
    const aantal = x ? getalOf0(x.aantal) : p.aantal;
    const korting = x ? getalOf0(x.korting) : p.korting;
    return {
      sleutel: productSleutel(soort, p.id),
      soort,
      bronId: p.id,
      voor,
      omschrijving: x ? x.naam : p.naam,
      groep: p.groep,
      prijs,
      aantal,
      btw: x ? getalOf0(x.btw) : p.btwPercentage,
      korting,
      totaal: x ? (prijs ?? 0) * (aantal ?? 0) - (korting ?? 0) : p.totaal,
      verwijderd: !!x?.verwijderd,
    };
  };
  const nieuwProduct = (n: NieuwProductConcept, voor: string): ProductRegel => {
    const prijs = getalOf0(n.prijs);
    const aantal = getalOf0(n.aantal);
    const korting = getalOf0(n.korting);
    return {
      sleutel: n.sleutel,
      soort: "NIEUW",
      bronId: 0,
      voor,
      omschrijving: n.naam,
      groep: null,
      prijs,
      aantal,
      btw: getalOf0(n.btw),
      korting,
      totaal: prijs * aantal - korting,
      verwijderd: false,
    };
  };
  const nieuwVoor = (dierId: number | null) => (c ? c.nieuweProducten.filter((n) => n.dierId === dierId) : []);

  for (const d of r.dieren) {
    const voor = d.naam ?? LEEG;
    const naam = verblijfNaam(d.prijsCategorie);
    const laag = d.dagenLaag ?? 0;
    const hoog = d.dagenHoog ?? 0;
    const x = c?.dieren[d.regelId];
    // Het verblijf is zijn eigen soort; de kolom ernaast zegt dat, zoals bij de producten.
    const basis = {
      soort: "VERBLIJF" as const, bronId: d.regelId, voor, btw: d.btwPercentage,
      groep: t("pensionKlant.res.verblijfRegel"), verwijderd: !!x?.verwijderd,
    };
    if (c && x) {
      // Bewerkt: één regel, want de api rekent alles tegen de dagprijs (hoogseizoendagen kwamen nooit voor).
      const aantal = conceptDagen(r, c);
      const prijs = getalOf0(x.prijs);
      const korting = getalOf0(x.korting);
      regels.push({ ...basis, sleutel: `v${d.regelId}`, omschrijving: naam, prijs, aantal, korting,
        totaal: aantal * prijs + (d.toeslag ?? 0) - korting });
    } else if (hoog > 0 && laag > 0) {
      regels.push({ ...basis, sleutel: `v${d.regelId}l`, omschrijving: naam, prijs: d.prijsLaag, aantal: laag, korting: null,
        totaal: laag * (d.prijsLaag ?? 0) });
      regels.push({ ...basis, sleutel: `v${d.regelId}h`, omschrijving: t("pensionKlant.res.verblijfHoog", { args: [naam] }),
        prijs: d.prijsHoog, aantal: hoog, korting: d.korting, totaal: d.verblijfBedrag - laag * (d.prijsLaag ?? 0) });
    } else {
      regels.push({ ...basis, sleutel: `v${d.regelId}`,
        omschrijving: hoog > 0 ? t("pensionKlant.res.verblijfHoog", { args: [naam] }) : naam,
        prijs: hoog > 0 ? d.prijsHoog : d.prijsLaag, aantal: hoog > 0 ? hoog : laag || r.gereserveerd.dagen,
        korting: d.korting, totaal: d.verblijfBedrag });
    }
    for (const p of d.producten) {
      // Een product bij een dier dat van de reservering gaat, gaat mee.
      const regel = product("DIER", p, voor);
      regels.push(x?.verwijderd ? { ...regel, verwijderd: true } : regel);
    }
    if (d.dierId != null) for (const n of nieuwVoor(d.dierId)) regels.push(nieuwProduct(n, voor));
  }
  if (c) {
    for (const n of c.nieuweDieren) {
      const dier = klantDieren.get(n.dierId);
      const voor = dier?.naam ?? LEEG;
      const aantal = conceptDagen(r, c);
      const prijs = getalOf0(n.prijs);
      const korting = getalOf0(n.korting);
      regels.push({
        sleutel: `vn${n.dierId}`,
        soort: "VERBLIJF",
        groep: t("pensionKlant.res.verblijfRegel"),
        bronId: 0,
        nieuwDierId: n.dierId,
        voor,
        omschrijving: verblijfNaam(dier?.prijs?.categorie),
        prijs,
        aantal,
        btw: verblijfBtw,
        korting,
        totaal: aantal * prijs - korting,
        verwijderd: false,
      });
      for (const p of nieuwVoor(n.dierId)) regels.push(nieuwProduct(p, voor));
    }
  }
  for (const p of r.producten) regels.push(product("RESERVERING", p, t("pensionKlant.res.reserveringRegel")));
  for (const n of nieuwVoor(null)) regels.push(nieuwProduct(n, t("pensionKlant.res.reserveringRegel")));
  return regels;
}

type Veld = "naam" | "prijs" | "aantal" | "btw" | "korting";

function ProductenBlok({
  r,
  ctx,
  klantDieren,
  bewerker,
  onStart,
  onToevoegen,
}: {
  r: PensionReserveringDetail;
  ctx: Context;
  klantDieren: KlantDieren;
  bewerker: Bewerker | null;
  onStart?: (aanpassen?: (c: Concept) => Concept) => void;
  onToevoegen?: () => void;
}) {
  const { t, locale } = ctx;
  const c = bewerker?.concept ?? null;
  const regels = productRegels(r, ctx, c, klantDieren);
  const getal = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const verwijder = (sleutel: string) => (x: Concept) => ({
    ...x,
    producten: { ...x.producten, [sleutel]: { ...x.producten[sleutel], verwijderd: true } },
  });
  // Voor wie een nieuw product kan zijn: de dieren op de reservering, ook die erbij komen.
  const dierKeuzes = [
    ...r.dieren
      .filter((d) => d.dierId != null && !c?.dieren[d.regelId]?.verwijderd)
      .map((d) => ({ dierId: d.dierId!, naam: d.naam ?? LEEG })),
    ...(c?.nieuweDieren ?? []).map((n) => ({ dierId: n.dierId, naam: klantDieren.get(n.dierId)?.naam ?? LEEG })),
  ];
  // Per regel: welke velden te wijzigen zijn, hoe dat gaat, en wat er in de actiekolom hoort. Dit
  // stond in de rij-map van de tabel; een tabel met kolomdefinities zou het per cel opnieuw doen.
  const productRijen = regels.map((p) => {
    const wie = `${p.omschrijving} (${p.voor})`;
    const nieuwP = p.soort === "NIEUW" ? c?.nieuweProducten.find((n) => n.sleutel === p.sleutel) : undefined;
    const nieuwD = p.nieuwDierId != null ? c?.nieuweDieren.find((n) => n.dierId === p.nieuwDierId) : undefined;
    const x = p.soort === "DIER" || p.soort === "RESERVERING" ? c?.producten[p.sleutel] : undefined;
    const dier = p.soort === "VERBLIJF" && p.nieuwDierId == null ? c?.dieren[p.bronId] : undefined;

    let waarden: Partial<Record<Veld, string>> | null = null;
    let zet: ((veld: Veld, waarde: string) => void) | null = null;
    if (bewerker && !p.verwijderd) {
      if (nieuwP) {
        waarden = nieuwP;
        zet = (veld, waarde) =>
          bewerker.zet((y) => ({
            ...y,
            nieuweProducten: y.nieuweProducten.map((n) => (n.sleutel === nieuwP.sleutel ? { ...n, [veld]: waarde } : n)),
          }));
      } else if (x) {
        waarden = x;
        zet = (veld, waarde) => bewerker.zetProduct(p.sleutel, veld, waarde);
      } else if (nieuwD) {
        waarden = { prijs: nieuwD.prijs, korting: nieuwD.korting };
        zet = (veld, waarde) =>
          bewerker.zet((y) => ({
            ...y,
            nieuweDieren: y.nieuweDieren.map((n) => (n.dierId === nieuwD.dierId ? { ...n, [veld]: waarde } : n)),
          }));
      } else if (dier && p.sleutel === `v${p.bronId}`) {
        waarden = { prijs: dier.prijs, korting: dier.korting };
        zet = (veld, waarde) => bewerker.zetDier(p.bronId, veld as "prijs" | "korting", waarde);
      }
    }
    const veld = (naam: Veld, label: string, smal?: boolean) =>
      waarden?.[naam] !== undefined && zet ? (
        <Getalveld smal={smal} label={t(label, { args: [wie] })} waarde={waarden[naam]!} onWijzig={(w) => zet!(naam, w)} />
      ) : null;

    let acties: ReactNode;
    if (nieuwP && bewerker) {
      acties = (
        <RijActies
          ctx={ctx}
          onVerwijder={() =>
            bewerker.zet((y) => ({ ...y, nieuweProducten: y.nieuweProducten.filter((n) => n.sleutel !== nieuwP.sleutel) }))
          }
        />
      );
    } else if (nieuwD && bewerker) {
      acties = (
        <RijActies
          ctx={ctx}
          onVerwijder={() =>
            bewerker.zet((y) => ({
              ...y,
              nieuweDieren: y.nieuweDieren.filter((n) => n.dierId !== nieuwD.dierId),
              nieuweProducten: y.nieuweProducten.map((n) => (n.dierId === nieuwD.dierId ? { ...n, dierId: null } : n)),
            }))
          }
        />
      );
    } else if (p.soort === "VERBLIJF") {
      // Het verblijf weghalen is het dier van de reservering halen.
      const regelDier = r.dieren.find((d) => d.regelId === p.bronId);
      const weg = verwijderDier(p.bronId, regelDier?.dierId ?? null);
      acties = (
        <RijActies
          ctx={ctx}
          onBewerk={!bewerker && onStart ? () => onStart() : undefined}
          onVerwijder={!r.ingecheckt && onStart ? () => (bewerker ? bewerker.zet(weg) : onStart(weg)) : undefined}
          verwijderd={p.verwijderd}
          onTerug={bewerker ? () => bewerker.zetDier(p.bronId, "verwijderd", false) : undefined}
          verwijderTitel={t("pensionKlant.res.bewerk.dierIngecheckt")}
        />
      );
    } else {
      // Weg omdat zijn dier van de reservering gaat: dat zet je terug bij het dier, niet hier.
      const viaDier = p.soort === "DIER" && p.verwijderd && !c?.producten[p.sleutel]?.verwijderd;
      acties = viaDier ? null : (
        <RijActies
          ctx={ctx}
          onBewerk={!bewerker && onStart ? () => onStart() : undefined}
          onVerwijder={
            onStart ? () => (bewerker ? bewerker.zet(verwijder(p.sleutel)) : onStart(verwijder(p.sleutel))) : undefined
          }
          verwijderd={p.verwijderd}
          onTerug={bewerker ? () => bewerker.zetProduct(p.sleutel, "verwijderd", false) : undefined}
        />
      );
    }

    return { p, wie, nieuwP, nieuwD, waarden, zet, veld, acties };
  });

  return (
    <Kaart
      titel={t("pensionKlant.res.producten")}
      icoon={Package}
      actie={
        onToevoegen ? (
          <button type="button" className="pk-rv-knop pk-rv-knop--accent" onClick={onToevoegen}>
            <Plus size={15} aria-hidden="true" />
            {t("pensionKlant.res.productToevoegen")}
          </button>
        ) : (
          <Knop icoon={Plus} titel={t("pensionKlant.res.bewerk.kanNiet")} className="pk-rv-knop--accent">
            {t("pensionKlant.res.productToevoegen")}
          </Knop>
        )
      }
    >
      {regels.length === 0 ? (
        <p className="pk-rv-leeg">{t("pensionKlant.res.geenProducten")}</p>
      ) : (
        <div className="pk-rv-tabelvak">
          <IndeelbareTabel
            tabelKey="pension-reservering-producten"
            columns={[
              {
                key: "dierReservering", header: t("pensionKlant.res.kol.dierReservering"),
                cell: ({ p, nieuwP }) => (
                  <span className="pk-rv-sterk">
                    {nieuwP && bewerker ? (
                      <select
                        className="pk-rv-selectie"
                        aria-label={t("pensionKlant.res.bewerk.voorVan", { args: [p.omschrijving] })}
                        value={nieuwP.dierId ?? ""}
                        onChange={(e) => {
                          const dierId = e.target.value === "" ? null : Number(e.target.value);
                          bewerker.zet((y) => ({
                            ...y,
                            nieuweProducten: y.nieuweProducten.map((n) => (n.sleutel === nieuwP.sleutel ? { ...n, dierId } : n)),
                          }));
                        }}
                      >
                        <option value="">{t("pensionKlant.res.reserveringRegel")}</option>
                        {dierKeuzes.map((k) => (
                          <option key={k.dierId} value={k.dierId}>
                            {k.naam}
                          </option>
                        ))}
                      </select>
                    ) : (
                      p.voor
                    )}
                  </span>
                ),
              },
              {
                key: "productDienst", header: t("pensionKlant.res.kol.productDienst"),
                cell: ({ p, wie, waarden, zet }) => (waarden?.naam !== undefined && zet ? (
                  <input
                    type="text"
                    className="pk-rv-bedraginvoer pk-rv-bedraginvoer--tekst"
                    aria-label={t("pensionKlant.res.bewerk.naamVan", { args: [wie] })}
                    maxLength={250}
                    value={waarden.naam}
                    onChange={(e) => zet("naam", e.target.value)}
                  />
                ) : (
                  p.omschrijving
                )),
              },
              {
                // De soort, los van de naam. "Parkwandeling" zegt niet dat het een arrangement is,
                // en in een lijst van elf regels is dat precies wat je zoekt.
                key: "omschrijving", header: t("pensionKlant.res.kol.omschrijving"),
                cell: ({ p }) => <span className="pk-rv-zacht">{p.groep ?? LEEG}</span>,
              },
              {
                key: "prijs", header: t("pensionKlant.res.kol.prijs"), align: "right",
                cell: ({ p, veld }) => (
                  <span className="pk-rv-nowrap">
                    {veld("prijs", "pensionKlant.res.bewerk.prijsVan") ?? formatEuro(p.prijs, locale)}
                  </span>
                ),
              },
              {
                key: "aantal", header: t("pensionKlant.res.kol.aantal"), align: "right",
                cell: ({ p, veld }) => veld("aantal", "pensionKlant.res.bewerk.aantalVan", true)
                  ?? (p.aantal != null ? getal.format(p.aantal) : LEEG),
              },
              {
                key: "btw", header: t("pensionKlant.res.kol.btw"), align: "right",
                cell: ({ p, veld }) => veld("btw", "pensionKlant.res.bewerk.btwVan", true)
                  ?? (p.btw != null ? `${getal.format(p.btw)}%` : LEEG),
              },
              {
                key: "korting", header: t("pensionKlant.res.kol.korting"), align: "right",
                cell: ({ p, veld }) => (
                  <span className="pk-rv-nowrap">
                    {veld("korting", "pensionKlant.res.bewerk.kortingVan") ?? formatEuro(p.korting ?? 0, locale)}
                  </span>
                ),
              },
              {
                key: "totaal", header: t("pensionKlant.res.kol.totaal"), align: "right",
                cell: ({ p }) => <span className="pk-rv-nowrap">{formatEuro(p.totaal, locale)}</span>,
              },
              {
                key: "acties", header: t("pensionKlant.res.kol.acties"), vast: true, align: "right",
                cell: ({ acties }) => acties,
              },
            ]}
            rows={productRijen}
            getRowKey={({ p }) => p.sleutel}
            rowClassName={({ p, nieuwP, nieuwD }) => (p.verwijderd ? "pk-rv-weg" : nieuwP || nieuwD ? "pk-rv-nieuwrij" : undefined)}
            empty=""
          />
        </div>
      )}
    </Kaart>
  );
}

/** Een verzekering "sluit" je af; al het andere voeg je toe. Zie {@link ReserveringsproductenKaart}. */
const VERZEKERING = /verzeker/i;

/**
 * De producten die bij de reservering zelf horen, niet bij een dier: verzekering, medifonds, borg.
 *
 * <p>Ze stonden alleen als regels onderin de productentabel, tussen de verblijven en de losse
 * producten van elk dier. Daar zijn ze niet te vinden als je wilt weten óf er een annuleringsverzekering
 * op deze boeking zit -- en dat is precies de vraag die aan de balie gesteld wordt. Ze blijven ook in
 * die tabel staan, want daar worden ze bewerkt en daar tellen ze op tot het totaal.
 *
 * <p><b>Het label is afgeleid, geen opgeslagen status.</b> De api kent bij een reserveringsproduct geen
 * toestand; een verzekering heet "afgesloten" omdat dat het woord is dat erbij hoort, de rest
 * "toegevoegd". Er staat dus niets op het scherm wat de database niet weet -- het is een andere
 * formulering van hetzelfde feit, namelijk dat het product erop staat.
 */
function ReserveringsproductenKaart({
  r,
  ctx,
  onBeheren,
}: {
  r: PensionReserveringDetail;
  ctx: Context;
  onBeheren?: () => void;
}) {
  const { t, locale } = ctx;
  if (r.producten.length === 0 && !onBeheren) {
    return null;
  }
  return (
    <Kaart
      titel={t("pensionKlant.res.reserveringsproducten")}
      icoon={Package}
      aantal={r.producten.length}
      actie={
        onBeheren && r.producten.length > 0 ? (
          <button type="button" className="pk-rv-knop" onClick={onBeheren}>
            {t("pensionKlant.res.beheren")}
          </button>
        ) : undefined
      }
    >
      {r.producten.length === 0 ? (
        <p className="pk-rv-leeg">{t("pensionKlant.res.geenReserveringsproducten")}</p>
      ) : (
        <ul className="pk-rv-respro">
          {r.producten.map((p) => (
            <li key={p.id}>
              <span className="pk-rv-respro__icoon" aria-hidden="true">
                {VERZEKERING.test(p.naam) || VERZEKERING.test(p.groep ?? "") ? (
                  <ShieldCheck size={16} />
                ) : (
                  <HeartPulse size={16} />
                )}
              </span>
              <span className="pk-rv-respro__tekst">
                <strong>{p.naam}</strong>
                <span className="pk-rv-respro__staat">
                  <Check size={13} aria-hidden="true" />
                  {t(
                    VERZEKERING.test(p.naam) || VERZEKERING.test(p.groep ?? "")
                      ? "pensionKlant.res.respro.afgesloten"
                      : "pensionKlant.res.respro.toegevoegd",
                  )}
                </span>
              </span>
              <span className="pk-rv-respro__bedrag">{formatEuro(p.totaal, locale)}</span>
            </li>
          ))}
        </ul>
      )}
      {onBeheren && (
        <button type="button" className="pk-rv-respro__toevoegen" onClick={onBeheren}>
          <Plus size={15} aria-hidden="true" />
          {t("pensionKlant.res.productToevoegen")}
        </button>
      )}
    </Kaart>
  );
}


function FinancieelBlok({
  r,
  ctx,
  concept,
  klantDieren,
}: {
  r: PensionReserveringDetail;
  ctx: Context;
  concept: Concept | null;
  klantDieren: KlantDieren;
}) {
  const { t, locale } = ctx;
  const f = r.financieel;
  const euro = (n: number) => formatEuro(n, locale);
  const regels = productRegels(r, ctx, concept, klantDieren).filter((p) => !p.verwijderd);
  const regelKorting = regels.reduce((s, p) => s + (p.korting ?? 0), 0);
  const subtotaal = regels.reduce((s, p) => s + p.totaal, 0) + regelKorting;
  // Tijdens het bewerken rekent het scherm vooruit zoals de api dat bij opslaan doet; prijzen zijn incl. BTW.
  const totaal = concept ? rond(regels.reduce((s, p) => s + p.totaal, 0)) : f.totaal;
  const btw = concept
    ? rond(regels.reduce((s, p) => s + (p.btw ? rond((p.totaal * p.btw) / (100 + p.btw)) : 0), 0))
    : f.btw;
  const exclBtw = concept ? rond(totaal - btw) : f.totaalExclBtw;
  const openstaand = concept ? rond(totaal - f.betaald) : f.openstaand;
  const percentages = [...new Set(regels.map((p) => p.btw).filter((b): b is number => b != null && b > 0))];
  const btwLabel =
    percentages.length === 1
      ? t("pensionKlant.res.fin.btwPercentage", { args: [`${percentages[0]}%`] })
      : t("pensionKlant.res.fin.btw");
  return (
    <Kaart titel={t("pensionKlant.res.financieel")} icoon={FileText}>
      <dl className="pk-rv-geld">
        <dt>{t("pensionKlant.res.fin.subtotaal")}</dt>
        <dd>{euro(subtotaal)}</dd>
        <dt>{t("pensionKlant.res.fin.klantkorting")}</dt>
        <dd>{euro(f.korting)}</dd>
        <dt>{t("pensionKlant.res.fin.kortingOverig")}</dt>
        <dd>{euro(regelKorting)}</dd>
        <dt>{t("pensionKlant.res.fin.exclBtw")}</dt>
        <dd>{euro(exclBtw)}</dd>
        <dt>{btwLabel}</dt>
        <dd>{euro(btw)}</dd>
        <dt data-sterk>{t("pensionKlant.res.fin.totaal")}</dt>
        <dd data-sterk>{euro(totaal)}</dd>
        <dt data-toon="groen">{t("pensionKlant.res.fin.betaald")}</dt>
        <dd data-toon="groen">{euro(f.betaald)}</dd>
        <dt data-toon={openstaand > 0 ? "rood" : undefined}>{t("pensionKlant.res.fin.openstaand")}</dt>
        <dd data-toon={openstaand > 0 ? "rood" : undefined}>{euro(openstaand)}</dd>
      </dl>
      <div className="pk-rv-geldtegels">
        {r.aanbetaling && (
          <div className="pk-rv-geldtegel" data-toon={r.aanbetaling.status === "VOLDAAN" ? "groen" : "amber"}>
            <Euro size={16} aria-hidden="true" />
            <span>
              <small>{t("pensionKlant.res.fin.aanbetalingVereist", { args: [`${r.aanbetaling.percentage}%`] })}</small>
              <strong>{euro(r.aanbetaling.vereist)}</strong>
            </span>
          </div>
        )}
      </div>
    </Kaart>
  );
}

// ── Rechterkolom ─────────────────────────────────────────────────────────────────────────────────────

function EerdereVerblijven({
  boekingen,
  gekozen,
  onKies,
  ctx,
}: {
  boekingen: Boeking[];
  gekozen: number;
  onKies: (id: number) => void;
  ctx: Context;
}) {
  const { t, locale } = ctx;
  const lijst = [...boekingen].sort((a, b) => b.van.localeCompare(a.van));
  return (
    <Kaart titel={t("pensionKlant.res.verblijven")} icoon={History} aantal={lijst.length} venster>
      {(sluit) => (
      <ul className="pk-rv-verblijven" aria-label={t("pensionKlant.res.verblijvenAria")}>
        {lijst.map((b) => {
          const namen = b.dieren.map((d) => d.naam).filter((n): n is string => !!n);
          const status = t(`pensionKlant.resstatus.${b.status}`);
          const datum = periode(b.van, b.tot, locale);
          return (
            <li key={b.reserveringId}>
              <button
                type="button"
                className="pk-rv-verblijf"
                aria-pressed={b.reserveringId === gekozen}
                aria-label={[datum, namen.join(", "), status].filter(Boolean).join(" · ")}
                onClick={() => { onKies(b.reserveringId); sluit(); }}
              >
                <span className="pk-rv-verblijf__datum">
                  <strong>{datum}</strong>
                  <small>
                    {b.nachten === 1
                      ? t("pensionKlant.res.nacht")
                      : t("pensionKlant.reserveringen.nachten", { args: [b.nachten] })}
                  </small>
                </span>
                <span className="pk-rv-verblijf__dieren">
                  {namen.map((n, i) => (
                    <span key={`${n}-${i}`} className="pk-rv-dierpil">
                      <PawPrint size={11} aria-hidden="true" />
                      {n}
                    </span>
                  ))}
                </span>
                <Pil toon={RES_TOON[b.status]}>{status}</Pil>
                <span className="pk-rv-verblijf__bedrag">
                  {b.totaalBedrag != null ? formatEuro(b.totaalBedrag, locale) : LEEG}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      )}
    </Kaart>
  );
}

function Bijzonderheden({
  r,
  klantDieren,
  ctx,
  onGewijzigd,
}: {
  r: PensionReserveringDetail;
  klantDieren: KlantDieren;
  ctx: Context;
  onGewijzigd: () => void;
}) {
  const { t } = ctx;
  const kenmerken = [
    ...new Set(r.dieren.flatMap((d) => (d.dierId != null ? (klantDieren.get(d.dierId)?.kenmerken ?? []) : []))),
  ];
  const notities = echteNotities(r);
  const [kiezen, setKiezen] = useState(false);
  return (
    <Kaart titel={t("pensionKlant.res.bijzonderheden")} icoon={FileText} inklapbaar>
      {notities.length === 0 ? (
        <p className="pk-rv-leeg">{t("pensionKlant.res.geenNotities")}</p>
      ) : (
        <div className="pk-rv-notities">
          {notities.slice(0, 6).map((n) => (
            <p key={n.id} title={[cijferDatum(n.datum), n.melder].filter(Boolean).join(" · ")}>
              {n.notitie}
            </p>
          ))}
        </div>
      )}
      <div className="pk-rv-tags pk-rv-tags--los">
        {(r.tags ?? []).map((tag) => (
          <span key={`tag-${tag.id}`} className="pk-rv-tag pk-rv-tag--eigen" style={{ "--tagkleur": tag.color } as CSSProperties}>
            {tag.label}
          </span>
        ))}
        {kenmerken.map((k) => (
          <span key={k} className="pk-rv-tag" data-toon={tagToon(kenmerkToon(k)) ?? "accent"}>
            {k}
          </span>
        ))}
        <span className="pk-rv-tagplek">
          <button
            type="button"
            className="pk-rv-tagknop"
            aria-expanded={kiezen}
            onClick={() => setKiezen((x) => !x)}
          >
            <Plus size={12} aria-hidden="true" />
            {t("pensionKlant.res.tagToevoegen")}
          </button>
          {kiezen && <TagKiezer r={r} ctx={ctx} onSluit={() => setKiezen(false)} onGewijzigd={onGewijzigd} />}
        </span>
      </div>
    </Kaart>
  );
}

/** Uit GET /api/pension/reserveringen/{id}/aanvraag. */
interface WizardAanvraag {
  concept: boolean;
  wizardVersie: number;
  antwoorden: { dierId: number | null; dier: string | null; elementId: string; vraag: string; type: string; waarde: string | null }[];
}

/**
 * Wat de wizard Nieuwe reservering bij deze reservering bewaarde: of het een concept is, en de antwoorden op de eigen
 * vragen van de vestiging. Een reservering van voor de wizard heeft geen aanvraag en toont niets.
 */
function UitDeWizard({ r, ctx }: { r: PensionReserveringDetail; ctx: Context }) {
  const { t, locale } = ctx;
  const { data } = useCustom<WizardAanvraag>({
    url: `pension/reserveringen/${r.id}/aanvraag`,
    method: "get",
    queryOptions: { retry: false },
  });
  const a = data?.data;
  if (!a || !Array.isArray(a.antwoorden) || (!a.concept && a.antwoorden.length === 0)) return null;
  return (
    <Kaart titel={t("pensionKlant.res.aanvraag.titel")} icoon={ClipboardList} inklapbaar>
      {a.concept && (
        <p className="pk-rv-leeg">
          <Pil toon="accent">{t("pensionKlant.res.aanvraag.concept")}</Pil>
        </p>
      )}
      {a.antwoorden.length > 0 && (
        <dl className="pk-rv-antwoorden">
          {a.antwoorden.map((x, i) => (
            <Fragment key={`${x.elementId}-${x.dierId ?? ""}-${i}`}>
              <dt>{x.dier ? `${x.vraag} – ${x.dier}` : x.vraag}</dt>
              <dd>{x.waarde ? antwoordTekst(x.type, x.waarde, t as never, locale) : LEEG}</dd>
            </Fragment>
          ))}
        </dl>
      )}
    </Kaart>
  );
}

/**
 * De tags van de reservering aan- en uitvinken; elke klik slaat meteen op. Tags horen niet bij het oude
 * pakket, dus bewerken of de versie van de reservering spelen hier niet mee.
 */
function TagKiezer({
  r,
  ctx,
  onSluit,
  onGewijzigd,
}: {
  r: PensionReserveringDetail;
  ctx: Context;
  onSluit: () => void;
  onGewijzigd: () => void;
}) {
  const { t } = ctx;
  const { data, isLoading, isError } = useCustom<ReserveringTag[]>({ url: "pension/reservering-tags", method: "get" });
  const { mutateAsync, isLoading: bezig } = useCustomMutation<PensionReserveringDetail>();
  const [gekozen, setGekozen] = useState(() => (r.tags ?? []).map((tag) => tag.id));
  const [fout, setFout] = useState(false);
  const alle = data?.data ?? [];

  const zet = async (id: number, aan: boolean) => {
    const vorige = gekozen;
    const volgende = aan ? [...vorige, id] : vorige.filter((x) => x !== id);
    setGekozen(volgende);
    setFout(false);
    try {
      await mutateAsync({
        url: `pension/reserveringen/${r.id}/tags`,
        method: "put",
        values: { tagIds: volgende },
        successNotification: false,
        errorNotification: false,
      });
      onGewijzigd();
    } catch {
      setGekozen(vorige);
      setFout(true);
    }
  };

  return (
    <div
      className="pk-rv-tagkiezer"
      role="group"
      aria-label={t("pensionKlant.res.tags.titel")}
      onKeyDown={(e) => {
        if (e.key === "Escape") onSluit();
      }}
    >
      <p className="pk-rv-tagkiezer__titel">{t("pensionKlant.res.tags.titel")}</p>
      {isLoading ? (
        <p className="pk-rv-leeg">{t("common.loading")}</p>
      ) : isError ? (
        <p className="pk-rv-kop__fout" role="alert">
          {t("pensionKlant.res.tags.laadfout")}
        </p>
      ) : alle.length === 0 ? (
        <p className="pk-rv-leeg">{t("pensionKlant.res.tags.geen")}</p>
      ) : (
        alle.map((tag) => (
          <label key={tag.id} className="pk-rv-tagkeuze">
            <input
              type="checkbox"
              checked={gekozen.includes(tag.id)}
              disabled={bezig}
              onChange={(e) => void zet(tag.id, e.target.checked)}
            />
            <span className="pk-rv-tagstip" style={{ background: tag.color }} aria-hidden="true" />
            {tag.label}
          </label>
        ))
      )}
      {fout && (
        <p className="pk-rv-kop__fout" role="alert">
          {t("pensionKlant.res.tags.mislukt")}
        </p>
      )}
    </div>
  );
}

/**
 * De geschiedenis van deze reservering: aangemaakt, bevestigd, ingecheckt, uitgecheckt, geannuleerd.
 *
 * <p><b>Dit is geen auditlogboek.</b> De api houdt van een reservering geen mutatiegeschiedenis bij; wat
 * hier staat zijn de momenten die op de reservering zelf bewaard worden, op volgorde gezet. Een
 * prijswijziging van gisteren staat er dus niet in, en een tussentijds verplaatste datum ook niet. De
 * kaart zegt dat er zelf bij, want een lijst die "geschiedenis" heet en gaten heeft is erger dan geen
 * lijst: dan gaat iemand ervan uit dat wat er niet in staat ook niet gebeurd is.
 *
 * <p>Bij in- en uitchecken staat erbij wie het deed, want dat weten we; bij aanmaken en bevestigen niet,
 * want dat bewaart de api niet en een naam verzinnen is erger dan een naam missen.
 */
interface VerstuurdItem {
  id: string;
  occurredAt: string;
  logType: string | null;
  status: string | null;
  templateName: string | null;
  sentByUser: string | null;
  recipient: string | null;
  generatedSubject: string | null;
}

function Geschiedenis({ r, klantId, ctx }: {
  r: PensionReserveringDetail;
  klantId?: number | null;
  ctx: Context;
}) {
  const { t } = ctx;
  // Wat er naar deze klant uitging: brieven, e-mails, afdrukken. De log hangt aan de relatie en niet
  // aan de reservering, dus dit is alles van deze klant -- dat staat er ook bij.
  const { data: verstuurd } = useCustom<{ items?: VerstuurdItem[] }>({
    url: "communicatie/log",
    method: "get",
    config: { query: { relatieId: klantId ?? 0, size: 50 } },
    queryOptions: { enabled: klantId != null, retry: false },
  });
  const momenten: {
    sleutel: string; moment: string; wat: string; wie?: string | null;
    naar?: string | null; soort?: string | null;
  }[] = [];
  if (r.aangemaakt) {
    momenten.push({ sleutel: "aangemaakt", moment: r.aangemaakt, wat: t("pensionKlant.res.gesch.aangemaakt") });
  }
  if (r.bevestigd) {
    momenten.push({ sleutel: "bevestigd", moment: r.bevestigd, wat: t("pensionKlant.res.gesch.bevestigd") });
  }
  if (r.incheck) {
    momenten.push({
      sleutel: "incheck",
      moment: r.incheck.moment,
      wat: t("pensionKlant.res.gesch.ingecheckt"),
      wie: r.incheck.door,
    });
  }
  if (r.uitcheck) {
    momenten.push({
      sleutel: "uitcheck",
      moment: r.uitcheck.moment,
      wat: t("pensionKlant.res.gesch.uitgecheckt"),
      wie: r.uitcheck.door,
    });
  }
  if (r.geannuleerdOp) {
    momenten.push({ sleutel: "geannuleerd", moment: r.geannuleerdOp, wat: t("pensionKlant.res.gesch.geannuleerd") });
  }
  for (const v of verstuurd?.data?.items ?? []) {
    momenten.push({
      sleutel: `verstuurd-${v.id}`,
      moment: v.occurredAt,
      wat: v.generatedSubject || v.templateName || t(`pensionKlant.res.gesch.soort.${v.logType ?? "overig"}`),
      wie: v.sentByUser,
      naar: v.recipient,
      soort: v.logType,
    });
  }
  if (momenten.length === 0) {
    return null;
  }
  // Nieuwste bovenaan: wat er laatst gebeurde is waar iemand naar zoekt.
  momenten.sort((a, b) => b.moment.localeCompare(a.moment));

  return (
    <Kaart titel={t("pensionKlant.res.geschiedenis")} icoon={History} aantal={momenten.length} venster>
      <ol className="pk-rv-gesch">
        {momenten.map((m) => (
          <li key={m.sleutel}>
            <span className="pk-rv-gesch__moment">{datumTijd(m.moment)}</span>
            <span className="pk-rv-gesch__wat">
              {m.soort && <Pil toon="accent" icoon={Mail}>{t(`pensionKlant.res.gesch.soort.${m.soort}`)}</Pil>}
              {m.wat}
              {m.wie && <span className="pk-rv-gesch__wie">{t("pensionKlant.res.gesch.door", { args: [m.wie] })}</span>}
              {m.naar && <span className="pk-rv-gesch__wie">{t("pensionKlant.res.gesch.naar", { args: [m.naar] })}</span>}
            </span>
          </li>
        ))}
      </ol>
      <p className="pk-rv-gesch__uitleg">{t("pensionKlant.res.gesch.uitleg")}</p>
    </Kaart>
  );
}


function Documenten({
  r,
  ctx,
  onOpenFactuur,
}: {
  r: PensionReserveringDetail;
  ctx: Context;
  onOpenFactuur?: (factuurId: string) => void;
}) {
  const { t } = ctx;
  const volgt = t("pensionKlant.res.volgt");
  const factuur = r.facturen[0];
  return (
    <Kaart titel={t("pensionKlant.res.documenten")} icoon={FileText} inklapbaar>
      <div className="pk-rv-docs">
        <Knop icoon={FileText} titel={volgt}>
          {t("pensionKlant.res.doc.bevestiging")}
        </Knop>
        {factuur && onOpenFactuur ? (
          <button
            type="button"
            className="pk-rv-knop"
            aria-label={t("pensionKlant.res.factuur", { args: [factuur.nummer ?? factuur.id] })}
            onClick={() => onOpenFactuur(String(factuur.id))}
          >
            <ReceiptText size={15} aria-hidden="true" />
            {t("pensionKlant.res.doc.factuur")}
          </button>
        ) : (
          <Knop icoon={ReceiptText} titel={t("pensionKlant.res.geenFactuur")}>
            {t("pensionKlant.res.doc.factuur")}
          </Knop>
        )}
        <Knop icoon={Sticker} titel={volgt}>
          {t("pensionKlant.res.doc.voedingssticker")}
        </Knop>
        <Knop icoon={FileSignature} titel={volgt}>
          {t("pensionKlant.res.doc.overeenkomst")}
        </Knop>
        <Knop icoon={CreditCard} titel={volgt}>
          {t("pensionKlant.res.doc.klantkaart")}
        </Knop>
      </div>
      <div className="pk-rv-docs pk-rv-docs--twee">
        <Knop icoon={MessageCircle} titel={volgt} toon="groen">
          {t("pensionKlant.res.doc.whatsapp")}
        </Knop>
        <Knop icoon={Mail} titel={volgt}>
          {t("pensionKlant.res.doc.email")}
        </Knop>
      </div>
    </Kaart>
  );
}

// ── Hulp ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * De notities die mensen schreven. Het oude pakket gebruikte {@code pension_reservering_notitie} ook als
 * logboek ("Gewijzigde gegevens: …", "De 'kennelkaart' is succesvol geprint."), en Fawkes schrijft er zijn
 * wijzigingen net zo in; die horen niet bij de bijzonderheden en niet op het signaal voor de receptie.
 */
function echteNotities(r: PensionReserveringDetail) {
  return r.notities.filter((n) => !isAutomatisch(n));
}

/** Grijze kenmerken zijn in het ontwerp gewone labels, zonder kleur. */
function tagToon(toon: string): Toon | undefined {
  return toon === "grijs" || toon === "groen" ? undefined : (toon as Toon);
}

/**
 * "18 nachten" of "18 dagen", naar wat de instelling doet.
 *
 * <p>De vestiging stelt in of er een extra dag bijkomt bij ophalen in de middag. Staat dat uit, dan is
 * het getal precies het aantal nachten; staat het aan en is er opgehaald in de middag, dan is het er
 * één meer en zijn het dagen. Het woord volgt dus de telling in plaats van andersom.
 *
 * <p>Afgeleid uit de getallen en niet uit de instelling nagerekend: de api telt, en het verschil tussen
 * wat hij teruggeeft en het aantal nachten tussen de datums zegt precies of die extra dag meetelde.
 * Zouden we de regel hier herhalen, dan kan hij uit de pas gaan lopen met de berekening waar de prijs op
 * gebaseerd is.
 */
function duur(periode: ReserveringPeriode, ctx: Context): string {
  const nachten = periode.van && periode.tot ? dagenTussen(periode.van, periode.tot) : periode.dagen;
  return periode.dagen > nachten ? dagen(periode.dagen, ctx) : nachtenTekst(periode.dagen, ctx);
}

function nachtenTekst(n: number, { t }: Context): string {
  return n === 1 ? t("pensionKlant.res.nacht") : t("pensionKlant.reserveringen.nachten", { args: [n] });
}

function dagen(n: number, { t }: Context): string {
  return n === 1 ? t("pensionKlant.res.dag") : t("pensionKlant.res.dagen", { args: [n] });
}

/** "12-12-2018 14:21" uit een ISO-tijdstip. */
function datumTijd(iso: string): string {
  const tijd = iso.length >= 16 ? iso.slice(11, 16) : "";
  return tijd ? `${cijferDatum(iso)} ${tijd}` : cijferDatum(iso);
}

/** "20-12-2018 09:04" uit een datum en een tijd als "09:04:00". */
function momentCijfer(datum: string, tijd: string | null): string {
  return tijd ? `${cijferDatum(datum)} ${tijd.slice(0, 5)}` : cijferDatum(datum);
}

function eersteRegel(tekst: string): string {
  const regel = tekst.split("\n")[0].trim();
  return regel.length > 60 ? `${regel.slice(0, 59)}…` : regel;
}
